import React, { useState, useRef, useContext, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Select, { components } from 'react-select';
import OutboundItem from './OutboundItem';
import FiltersDropdown from './FiltersDropdown';
import Box from './Box';
import BoxesPanel from './BoxesPanel';
import dayjs from 'dayjs';
import { fetchUpsMergedLabels } from './USAHelper/MergeUPSLabels';
import ImportModal from './ImportModal';
import { BoxContext } from './BoxContext';
import SplitShipmentModal from './SplitShipmentModal';
import { BoxProvider } from './BoxContext';
import SendEmail from './SendEmail';
import { RoleProvider, useRole } from './contexts/RoleContext';
import RoleSelector from './components/RoleSelector';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import NicknameModal from './NicknameModal';
import PickingListModal from './PickingListModal';
import BulkPrintModal from './BulkPrintModal';
import { update } from 'lodash';
// Radix UI and modern imports
import { 
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Card,
  CardContent,
  CardHeader,
  Progress,
  CircularProgress,
  Badge,
  Switch,
  Alert,
  AlertDescription,
  Separator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './components/ui';
// Lucide React icons
import {
  BarChart3 as AssessmentIcon,
  Clock as ScheduleIcon,
  Building2 as BusinessIcon,
  User as PersonIcon,
  Info as InfoIcon,
  Edit3 as EditIcon,
  Scissors as ContentCutIcon,
  Download as DownloadIcon,
  FileSpreadsheet as TableChartIcon,
  Upload as UploadIcon,
  Save as SaveIcon,
  Mail as EmailIcon,
  List as ListIcon,
  RefreshCw as RefreshIcon,
  Wand2 as AutoFixHighIcon,
  DollarSign as MoneyIcon,
  MoreVertical as MoreVertIcon,
  Settings as SettingsIcon,
  Printer as PrintIcon,
  ChevronLeft as ArrowBackIcon,
  ChevronRight as ArrowForwardIcon,
  ExternalLink as LaunchIcon,
  Loader2,
  ChevronUp,
  ChevronDown,
  Users,
  Package2,
  Tag,
  Camera,
  BookOpen,
} from 'lucide-react';
import ReassignPlanModal from './ReassignPlanModal';
import { VisionDetectorOverlay } from './components/VisionDetector';
import BookModeOverlay from './components/BookModeOverlay';
import useBarcodeScanner from './hooks/useBarcodeScanner';
import { generatePDF } from './labelGenerator';
import OptimizeModal from './OptimizeModal';
import moment from 'moment';
import BulkFeesModal from './BulkFeesModal';
import PlanPlacementFeesModal from './PlanPlacementFeesModal';
import PlanSelectionModal from './components/PlanSelectionModal';
import OutboundImportModal from './OutboundImportModal';
import { fetchAvailablePrinters } from './services/printService';

// Update API URL configuration for Vite
const API_URL = import.meta.env.VITE_API_URL || '/api';
const API_TOKEN = import.meta.env.VITE_API_TOKEN;

// Custom printer option component for react-select
const PrinterOption = (props) => {
  const { data, innerProps, innerRef } = props;
  
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 12px',
        cursor: 'pointer',
        backgroundColor: props.isFocused ? '#e2e2e2' : 'white',
        color: '#1a365d',
      }}
    >
      <div>{data.label}</div>
      {data.description && data.description !== data.label && (
        <div style={{ fontSize: '0.75rem', color: 'gray' }}>
          {data.description}
        </div>
      )}
    </div>
  );
};

/* 
  // This is our custom option component for React-Select 
  // It includes the "Cancel Plan" button if plan isActive === true
*/
const CustomOption = (props) => {
  const { data, innerProps, innerRef } = props; 
  // data structure example: { value, label, isActive: boolean }

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 10px',
        margin: '1px 0',
        cursor: 'pointer',
        backgroundColor: props.isSelected 
          ? '#3b82f6' 
          : props.isFocused 
            ? '#f3f4f6' 
            : 'transparent',
        color: props.isSelected ? 'white' : '#374151',
        borderRadius: '4px',
        fontSize: '0.8125rem',
        lineHeight: '1.2',
        fontWeight: props.isSelected ? '500' : '400',
      }}
    >
      <div style={{ marginRight: '8px', flex: 1 }}>{data.label}</div>

      {data.isActive && (
        <button
          style={{
            backgroundColor: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            padding: '2px 6px',
            cursor: 'pointer',
            fontSize: '0.6875rem',
            fontWeight: '500',
            flexShrink: 0,
          }}
          onClick={(e) => {
            e.stopPropagation();
            props.selectProps.handleCancelPlan(data.value); 
          }}
        >
          Cancel
        </button>
      )}
    </div>
  );
};

// Actions Menu Component
const ActionsMenu = ({
  onSplit,
  onBulkFees,
  onDownloadUPS,
  onExport,
  onImport,
  onImportFromOutbounds,
  onContact,
  onPickingList,
  onReset,
  onOptimize,
  onUSAHelper,
  onDebugRefresh,
  onPlanPlacementFees,
  onVisionScanner,
  isUSAShipment,
  // Book Mode props
  bookModeEnabled,
  onBookModeToggle,
  bookModePrinter,
  onBookModePrinterChange,
  bookModeAutoPrint,
  onBookModeAutoPrintToggle,
  availablePrinters,
  openBoxNumber,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
          <MoreVertIcon className="w-4 h-4 mr-2" />
          Actions
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Book Mode Section */}
        <div className="px-2 py-2 border-b border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium">Book Mode</span>
            </div>
            <Switch
              checked={bookModeEnabled}
              onCheckedChange={onBookModeToggle}
              disabled={bookModeAutoPrint && !bookModePrinter}
              className="scale-75"
            />
          </div>
          {bookModeEnabled && (
            <div className="text-xs text-green-600 flex items-center gap-1 mb-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Active - scanning enabled
            </div>
          )}
          {bookModeEnabled && !openBoxNumber && (
            <div className="text-xs text-amber-600 mb-1">Select a box to add items</div>
          )}
          {bookModeAutoPrint && !bookModePrinter && (
            <div className="text-xs text-amber-600 mb-1">Select a printer to enable</div>
          )}
          {/* Printer selector for Book Mode */}
          <select
            value={bookModePrinter || ''}
            onChange={(e) => onBookModePrinterChange(e.target.value || null)}
            className="w-full mt-1 text-xs border border-slate-300 rounded px-2 py-1 bg-white"
          >
            <option value="">Select Book Mode Printer</option>
            {availablePrinters?.map(printer => (
              <option key={printer.id} value={printer.id}>
                {printer.name}
              </option>
            ))}
          </select>
          {/* Auto Print toggle */}
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={bookModeAutoPrint}
              onChange={(e) => onBookModeAutoPrintToggle(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-xs text-slate-600">Auto-print labels</span>
          </label>
          {!bookModeAutoPrint && (
            <div className="text-xs text-slate-500 mt-1 ml-6">
              Labels will be saved as PDF
            </div>
          )}
        </div>

        <DropdownMenuItem onClick={onSplit} className="flex items-center">
          <ContentCutIcon className="w-4 h-4 mr-2" />
          Split Shipment
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onBulkFees} className="flex items-center">
          <MoneyIcon className="w-4 h-4 mr-2" />
          Add Bulk Fees
        </DropdownMenuItem>
        {isUSAShipment && (
          <DropdownMenuItem onClick={onPlanPlacementFees} className="flex items-center">
            <AssessmentIcon className="w-4 h-4 mr-2" />
            Plan Placement Fees
          </DropdownMenuItem>
        )}
        <Separator />
        <DropdownMenuItem onClick={onDownloadUPS} className="flex items-center">
          <DownloadIcon className="w-4 h-4 mr-2" />
          UPS Labels
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onExport} className="flex items-center">
          <TableChartIcon className="w-4 h-4 mr-2" />
          Export Excel
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onImport} className="flex items-center">
          <UploadIcon className="w-4 h-4 mr-2" />
          Import Excel
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onImportFromOutbounds} className="flex items-center">
          <RefreshIcon className="w-4 h-4 mr-2" />
          Sync from Outbounds
        </DropdownMenuItem>
        <Separator />
        <DropdownMenuItem onClick={onContact} className="flex items-center">
          <EmailIcon className="w-4 h-4 mr-2" />
          Contact Client
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onPickingList} className="flex items-center">
          <ListIcon className="w-4 h-4 mr-2" />
          Picking List
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onVisionScanner} className="flex items-center">
          <Camera className="w-4 h-4 mr-2" />
          Vision Scanner
        </DropdownMenuItem>
        <Separator />
        <DropdownMenuItem onClick={onOptimize} className="flex items-center">
          <AutoFixHighIcon className="w-4 h-4 mr-2" />
          Optimize
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onUSAHelper} className="flex items-center">
          <LaunchIcon className="w-4 h-4 mr-2" />
          USA Helper
        </DropdownMenuItem>
        <Separator />
        <DropdownMenuItem onClick={onDebugRefresh} className="flex items-center text-blue-600 focus:text-blue-600">
          <RefreshIcon className="w-4 h-4 mr-2" />
          Refresh Data
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onReset} className="flex items-center text-red-600 focus:text-red-600">
          <RefreshIcon className="w-4 h-4 mr-2" />
          Reset Boxes
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Shipment = () => {
  const { shipmentId } = useParams();

  return (
    <RoleProvider>
      <BoxProvider shipmentId={shipmentId}>
        <ShipmentContent />
      </BoxProvider>
    </RoleProvider>
  );
};

const ShipmentContent = () => {
  // All hooks and state declarations first
  const { canViewLeftPanel, isBoxPanelReadOnly, canEditLeftPanel, currentRole } = useRole();
  const fileInputRef = useRef(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { shipmentId } = useParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [showGeneratePlanModal, setShowGeneratePlanModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [hasCheckedPlans, setHasCheckedPlans] = useState(false);
  const [showBulkFeesModal, setShowBulkFeesModal] = useState(false);
  const [showPlanPlacementFeesModal, setShowPlanPlacementFeesModal] = useState(false);
  const [isEditingWarehouseNotes, setIsEditingWarehouseNotes] = useState(false);
  
  // Auto print feature
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(false);

  // Book Mode state
  const [bookModeEnabled, setBookModeEnabled] = useState(false);
  const [bookModePrinter, setBookModePrinter] = useState(null);
  const [bookModeAutoPrint, setBookModeAutoPrint] = useState(() => {
    // Default to true for backwards compatibility
    const saved = localStorage.getItem('bookModeAutoPrint');
    return saved === null ? true : saved === 'true';
  });
  const [showBookModeOverlay, setShowBookModeOverlay] = useState(false);
  const [bookModeResult, setBookModeResult] = useState(null);

  // Refs to track current box state for Book Mode (avoids stale closure issues)
  const openBoxNumberRef = useRef(null);
  const selectedBoxRef = useRef(null);

  // Book Mode sound effects
  const playBookModeSuccess = useCallback(() => {
    try {
      const audio = new Audio('/sounds/success-scan.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => console.log('Audio play prevented:', err));
    } catch (err) {
      console.log('Sound file not found:', err.message);
    }
  }, []);

  const playBookModeError = useCallback(() => {
    try {
      const audio = new Audio('/sounds/error-scan.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => console.log('Audio play prevented:', err));
    } catch (err) {
      console.log('Sound file not found:', err.message);
    }
  }, []);

    // Resizable column hooks
  const [leftColumnWidth, setLeftColumnWidth] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const resizerRef = useRef(null);

  // Move all other state declarations here...
  const [filterByPackingGroup, setFilterByPackingGroup] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);
  const [showSplitShipmentModal, setShowSplitShipmentModal] = useState(false);
  const [selectedSplitItems, setSelectedSplitItems] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newOutboundId, setNewOutboundId] = useState(null);

  // Add showSnackbar function here
  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };



  // Toggle auto print function
  const handleAutoPrintToggle = (checked) => {
    setAutoPrintEnabled(checked);
  };

  // Load auto print setting from localStorage
  useEffect(() => {
    const savedAutoPrint = localStorage.getItem('autoPrintEnabled');
    if (savedAutoPrint === 'true') {
      setAutoPrintEnabled(true);
    }
  }, []);

  // Save auto print setting to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('autoPrintEnabled', autoPrintEnabled);
  }, [autoPrintEnabled]);

  // Load book mode settings from localStorage
  useEffect(() => {
    const savedBookMode = localStorage.getItem('bookModeEnabled');
    const savedBookModePrinter = localStorage.getItem('bookModePrinter');
    if (savedBookMode === 'true') {
      setBookModeEnabled(true);
    }
    if (savedBookModePrinter) {
      setBookModePrinter(savedBookModePrinter);
    }
  }, []);

  // Save book mode settings to localStorage
  useEffect(() => {
    localStorage.setItem('bookModeEnabled', bookModeEnabled.toString());
  }, [bookModeEnabled]);

  useEffect(() => {
    if (bookModePrinter) {
      localStorage.setItem('bookModePrinter', bookModePrinter);
    }
  }, [bookModePrinter]);

  useEffect(() => {
    localStorage.setItem('bookModeAutoPrint', bookModeAutoPrint.toString());
  }, [bookModeAutoPrint]);

    // Move the resize callbacks up here, before any other functions
  const startResizing = useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback((e) => {
    if (isResizing) {
      const containerWidth = document.querySelector('.resizable-container').offsetWidth;
      const newWidth = (e.clientX / containerWidth) * 100;
      if (newWidth > 20 && newWidth < 80) {
        setLeftColumnWidth(newWidth);
      }
    }
  }, [isResizing]);

  // Move all useEffects up here as well
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  useEffect(() => {
    if (isResizing) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
  }, [isResizing]);

  // Context
  const {
    shipmentInfo,
    shipmentItems,
    shelvingData,
    reloadShipmentData,
    servicesData,
    clientsData,
    clientInfo,
    fetchAsinData,
    loading,
    removeShelf,
    updateShelvingQty,
    removeShelving,
    fetchAllData,
    boxes,
    setSelectedNickname,
    selectedNickname,
    setBoxes,
    fetchStandardBoxes,
    refreshBoxes,
    plans,
    selectedPlan,
    setSelectedPlan,
    setSelectedPackingGroup,
    selectedPackingGroup,
    packingGroups,
    asinData: contextAsinData,
    standardBoxes,
    fdaClassifications
  } = useContext(BoxContext);

  // Auto-refresh box contents every 10 seconds when user is in label coordinator mode
  useEffect(() => {
    if (isBoxPanelReadOnly()) {
      const intervalId = setInterval(() => {
        refreshBoxes();
      }, 10000); // 10 seconds

      return () => clearInterval(intervalId);
    }
  }, [isBoxPanelReadOnly, refreshBoxes]);

  // Add this line in ShipmentContent component
  const [openBoxNumber, setOpenBoxNumber] = useState(null);
  const [oversizedItemsWithoutFees, setOversizedItemsWithoutFees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showShelfReminder, setShowShelfReminder] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [isClientNotesExpanded, setIsClientNotesExpanded] = useState(false);

  const [asinData, setAsinData] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);

  // Keep refs in sync with state for Book Mode (avoids stale closure issues)
  useEffect(() => {
    openBoxNumberRef.current = openBoxNumber;
  }, [openBoxNumber]);

  useEffect(() => {
    selectedBoxRef.current = selectedBox;
  }, [selectedBox]);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [boxSearchTerm, setBoxSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(['All']);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [importedData, setImportedData] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showOutboundImportModal, setShowOutboundImportModal] = useState(false);
  const [showPickingListModal, setShowPickingListModal] = useState(false);
  const [pickingListData, setPickingListData] = useState([]);
  const [showItemsForAllPlacements, setShowItemsForAllPlacements] = useState(false);
  const [showReassignPlanModal, setShowReassignPlanModal] = useState(false);
  const [mainOutboundId, setMainOutboundId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  // Prep requirements state
  const [prepRequirements, setPrepRequirements] = useState({});
  const [isLoadingPrep, setIsLoadingPrep] = useState(false);
  
  // Printer state - centralized to avoid multiple API calls
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  const [showPlanSelectionModal, setShowPlanSelectionModal] = useState(false);
  
  // State for packing group filter
  const [selectedPackingGroupFilter, setSelectedPackingGroupFilter] = useState(null);

  // Add state for validation errors modal
  const [showValidationErrorsModal, setShowValidationErrorsModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidationConfirmed, setIsValidationConfirmed] = useState(false);
  const [currentSubmissionPlan, setCurrentSubmissionPlan] = useState(null);
  
  // Loading states for service operations
  const [loadingServices, setLoadingServices] = useState({});
  const [completedServices, setCompletedServices] = useState({});
  const [isRefreshingData, setIsRefreshingData] = useState(false);

  // Bulk selection state for printing
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showBulkPrintModal, setShowBulkPrintModal] = useState(false);

  // Vision detector state
  const [showVisionDetector, setShowVisionDetector] = useState(false);
  const [visionDetectedItems, setVisionDetectedItems] = useState([]);

  // Function to fetch available printers (centralized)
  const fetchPrinters = async () => {
    try {
      setLoadingPrinters(true);
      const printers = await fetchAvailablePrinters();
      setAvailablePrinters(printers);
    } catch (error) {
      console.error('Error fetching printers:', error);
      // Fallback to empty list
      setAvailablePrinters([]);
    } finally {
      setLoadingPrinters(false);
    }
  };



  // Fetch printers once on component mount
  useEffect(() => {
    fetchPrinters();
  }, []);

  // Load internal notes when shipmentInfo is available
  useEffect(() => {
    if (shipmentInfo?.internal_notes) {
      setInternalNotes(shipmentInfo.internal_notes);
    }
  }, [shipmentInfo]);

  // Auto-resize textareas based on content
  useEffect(() => {
    const resizeTextarea = (textarea) => {
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 160) + 'px';
      }
    };

    // Resize client notes
    const clientNotesTextarea = document.querySelector('textarea[readonly]');
    if (clientNotesTextarea) {
      resizeTextarea(clientNotesTextarea);
    }

    // Resize warehouse notes
    const warehouseNotesTextarea = document.querySelector('textarea:not([readonly])');
    if (warehouseNotesTextarea) {
      resizeTextarea(warehouseNotesTextarea);
    }
  }, [shipmentInfo?.notes, internalNotes]);

  // State for progress
  const [unitProgress, setUnitProgress] = useState(0);
  const [skuProgress, setSkuProgress] = useState(0);
  const [unitsPacked, setUnitsPacked] = useState(0);
  const [skusCompleted, setSkusCompleted] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [totalSKUs, setTotalSKUs] = useState(0);

  // Check for missing Oversized fees
  const checkOversizedItemsWithoutFees = () => {
    console.log("=== OVERSIZED FEE CHECK STARTED ===");
    console.log("Current shipmentItems count:", shipmentItems?.length);
    console.log("Current completed services:", completedServices);
    console.log("Current loading services:", loadingServices);
    
    if (!shipmentItems || shipmentItems.length === 0) {
      console.log("No shipmentItems available for checking");
      return true;
    }

    const itemsWithoutFees = shipmentItems.filter(item => {
      const lengthInches = item.item.length_mm / 25.4;
      const widthInches = item.item.width_mm / 25.4;
      const heightInches = item.item.height_mm / 25.4;
      const weightLbs = item.item.weight_gm / 453.592;

      const isOversized = lengthInches > 18 || widthInches > 14 || heightInches > 8 || weightLbs > 20;
      const isHugeOversized = isOversized && (lengthInches >= 25 || widthInches >= 25 || heightInches >= 25);

      let expectedFee = '';
      if (isHugeOversized) expectedFee = 'Huge Oversized';
      else if (isOversized) expectedFee = 'Oversized';

      if (expectedFee) {
        console.log(`\n📦 Item: ${item.item.title} (SKU: ${item.item.merchant_sku})`);
        console.log(`   Dimensions: ${lengthInches.toFixed(1)}" x ${widthInches.toFixed(1)}" x ${heightInches.toFixed(1)}" | Weight: ${weightLbs.toFixed(1)} lbs`);
        console.log(`   Expected fee: ${expectedFee}`);
        console.log(`   All services on item:`, item.service_lines?.map(sl => ({
          name: sl.service.name,
          charge: sl.service.charge,
          id: sl.service.id
        })) || []);

        // More flexible fee matching
        const oversizedKeywords = ['oversized', 'oversize'];
        const hugeKeywords = ['huge', 'huge oversized'];
        
        const hasFee = item.service_lines?.some(serviceLine => {
          const serviceName = serviceLine.service.name?.toLowerCase() || '';
          const serviceCharge = serviceLine.service.charge;
          
          // Check if service has any charge (not null, undefined, or 0)
          const hasValidCharge = serviceCharge !== null && 
                                serviceCharge !== undefined && 
                                serviceCharge !== 0 && 
                                serviceCharge !== '0';
          
          let isMatch = false;
          
          if (expectedFee === 'Huge Oversized') {
            // For huge oversized, accept either exact match or services containing "huge"
            isMatch = serviceName === 'huge oversized' || 
                     hugeKeywords.some(keyword => serviceName.includes(keyword));
          } else if (expectedFee === 'Oversized') {
            // For regular oversized, match "oversized" but not "huge oversized"
            isMatch = (serviceName === 'oversized' || 
                      oversizedKeywords.some(keyword => serviceName.includes(keyword))) &&
                      !hugeKeywords.some(keyword => serviceName.includes(keyword));
          }
          
          console.log(`   🔍 Checking service: "${serviceLine.service.name}" | Match: ${isMatch} | Valid charge: ${hasValidCharge} | Charge: ${serviceCharge}`);
          
          return isMatch && hasValidCharge;
        });
        
        if (!hasFee) {
          console.log(`   ❌ MISSING expected fee: ${expectedFee}`);
          return true; 
        } else {
          console.log(`   ✅ HAS expected fee: ${expectedFee}`);
        }
      }
      return false; 
    });

    console.log(`\n=== OVERSIZED FEE CHECK RESULTS ===`);
    console.log(`Items without fees: ${itemsWithoutFees.length}`);

    if (itemsWithoutFees.length > 0) {
      console.log("❌ Items missing oversized fees:", itemsWithoutFees.map(item => ({
        sku: item.item.merchant_sku,
        title: item.item.title,
        dimensions: `${(item.item.length_mm / 25.4).toFixed(1)}" x ${(item.item.width_mm / 25.4).toFixed(1)}" x ${(item.item.height_mm / 25.4).toFixed(1)}"`,
        weight: `${(item.item.weight_gm / 453.592).toFixed(1)} lbs`,
        currentServices: item.service_lines?.map(sl => sl.service.name) || []
      })));
      setOversizedItemsWithoutFees(itemsWithoutFees);
      // Only reset states when first opening modal, not during refreshes
      if (!isModalOpen) {
        setLoadingServices({});
        setCompletedServices({});
      }
      setIsModalOpen(true);
      return false; 
    }

    console.log("✅ All oversized items have the correct fees applied.");
    return true; 
  };

  // Replace the existing plans check useEffect
  useEffect(() => {
    if (shipmentInfo && !hasCheckedPlans) {
      const timer = setTimeout(() => {
        const checkPlans = () => {
          const hasNoPlans = !plans || plans.length === 0;
          const hasShipmentInfo = Boolean(shipmentInfo);
          const shouldShowModal = hasShipmentInfo && hasNoPlans;
          
          if (shouldShowModal) {
            setShowGeneratePlanModal(true);
          }
        };

        checkPlans();
        setHasCheckedPlans(true);
      }, 3000); // 3 second delay

      return () => clearTimeout(timer);
    }
  }, [shipmentInfo, plans, hasCheckedPlans]);

  // Reset hasCheckedPlans when shipmentId changes
  useEffect(() => {
    setHasCheckedPlans(false);
    setIsValidationConfirmed(false);
    setCurrentSubmissionPlan(null);
  }, [shipmentId]);

  // Fetch prep requirements for all items in the shipment
  useEffect(() => {
    const fetchPrepRequirements = async () => {
      if (!shipmentItems || shipmentItems.length === 0 || !clientInfo?.id) return;

      // Extract all MSKUs from the shipment
      const mskus = shipmentItems.map(shipmentItem => shipmentItem.item.merchant_sku);
      
      if (mskus.length === 0) return;

      try {
        setIsLoadingPrep(true);

        const queryParams = new URLSearchParams();
        queryParams.append('teamId', clientInfo.id);
        mskus.forEach(msku => queryParams.append('mskus[]', msku));

        const response = await fetch(`${import.meta.env.VITE_SERVER_API_URL || 'http://localhost:5000'}/api/item-prep-details?${queryParams.toString()}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch prep requirements');
        }

        const data = await response.json();
        console.log('Fetched prep requirements for shipment:', data.prepRequirements);
        
        setPrepRequirements(data.prepRequirements);
        
      } catch (error) {
        console.error('Error fetching prep requirements:', error.message);
      } finally {
        setIsLoadingPrep(false);
      }
    };

    fetchPrepRequirements();
  }, [shipmentItems, clientInfo?.id]);

  // NEW: Check for multiple ACTIVE plans
  useEffect(() => {
    if (!plans || plans.length === 0) return;

    const activePlans = plans.filter(plan => plan.status === 'ACTIVE');
    if (activePlans.length > 1) {
      console.warn('Warning: There are multiple ACTIVE plans. Only one plan should be active at a time. Please cancel extra active plan(s) before proceeding.');
      // Or use showSnackbar(...) if you prefer
      // showSnackbar('Multiple active plans detected. Please review.', 'warning');
    }
  }, [plans]);

  const handleResetBoxContents = async () => {
    try {
      setIsResetting(true);
      // Use outbound-based endpoint that doesn't require plan selection
      const response = await fetch(`${API_URL}/api/reset_box_contents_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outbound_id: shipmentId, 
          employee: selectedNickname,
        }),
      });

      if (response.ok) {
        console.log('Reset successful, refreshing boxes...');
        await refreshBoxes();
        showSnackbar('Box contents reset successfully', 'success');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Reset response not ok:', errorData);
        // Still refresh since the deletion might have worked
        console.log('Refreshing boxes anyway...');
        await refreshBoxes();
        showSnackbar('Reset completed (check if boxes were cleared)', 'warning');
      }
    } catch (error) {
      console.error('Error resetting box contents:', error);
      // Still refresh to see current state
      console.log('Refreshing boxes after error...');
      await refreshBoxes();
      showSnackbar('Reset request failed, but refreshing data', 'error');
    } finally {
      setIsResetting(false);
      setShowResetDialog(false);
    }
  };

  // CHANGED: handle Plan selection
  const handlePlanSelect = async (selectedOption) => {
    if (plans.length === 1) {
      const plan = plans[0];
      setSelectedPlan(plan);
    } else {
      const plan = plans.find(p => p.id === selectedOption.value);
      setSelectedPlan(plan ? plan : null);
    }
    
    await refreshBoxes();
  };

  // NEW: handleCancelPlan method
  const handleCancelPlan = async (planId) => {
    const confirmCancel = window.confirm(`Are you sure you want to cancel Plan: ${planId}?`);
    if (!confirmCancel) return;

    try {
      // Post to your cancel endpoint
      const response = await fetch(
        `https://app.yyzprep.ca/api/fba-transport/v2024/plans/${planId}/cancel-plan?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({plan_id : planId}),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to cancel plan with ID ${planId}`);
      }

      // Refresh to see updated plan statuses
      await reloadShipmentData();
      showSnackbar(`Plan ${planId} cancelled successfully!`, 'success');
    } catch (error) {
      console.error('Error cancelling plan:', error);
      showSnackbar(`Error cancelling plan: ${error.message}`, 'error');
    }
  };

  const handlePackingGroupSelect = (selectedOption) => {
    setSelectedPackingGroup(selectedOption.value);
  };

  const validateBoxDimensions = (boxes) => {
    const missingDimensions = [];
    boxes.forEach(box => {
      if (!box.height || !box.width || !box.length || !box.weight) {
        missingDimensions.push(
          `Box ${box.boxNumber} - missing ${
            !box.length ? 'Length' : ''
          }${!box.width ? 'Width' : ''}${!box.height ? 'Height' : ''}${
            !box.weight ? 'Weight' : ''
          }`
        );
      }
    });
    return missingDimensions;
  };

  const handleSplitShipment = async () => {
    if (selectedSplitItems.length === 0) {
      alert("Please select at least one item to split.");
      return;
    }

    try {
      // Step 1: Create a new outbound
      const newOutboundResponse = await fetch(`${API_URL}/api/create_new_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${shipmentInfo.name} | Split`,
          warehouse_id: shipmentInfo.warehouse_id,
          notes: `This outbound was split from ${shipmentInfo.id} on ${new Date().toLocaleString('en-US', {
            timeZone: 'America/New_York'
          })}`,
          team_id: shipmentInfo.team_id
        }),
      });

      if (!newOutboundResponse.ok) {
        throw new Error('Failed to create new outbound');
      }

      const newOutboundData = await newOutboundResponse.json();
      const newOutboundId = newOutboundData.shipment_id;
      setNewOutboundId(newOutboundId);

      // Step 2: Add selected items to the new outbound
      for (const item of selectedSplitItems) {
        const addItemResponse = await fetch(`${API_URL}/api/add_item_to_outbound`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shipment_id: newOutboundId,
            item_id: item.item_id,
            quantity: item.quantity,
            expiry_date: item.expiry_date,
          }),
        });
        if (!addItemResponse.ok) {
          throw new Error(`Failed to add item ${item.item_id} to new outbound`);
        }
      }

      // Step 3: Confirm items are added
      const confirmResponse = await fetch(`${API_URL}/api/confirm_items_added`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipment_id: newOutboundId }),
      });
      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm items added to new outbound');
      }

      // Step 4: Remove items from old outbound
      for (const item of selectedSplitItems) {
        const deleteItemResponse = await fetch(`${API_URL}/api/delete_item_from_outbound`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shipment_id: shipmentId,
            item_id: item.id,
          }),
        });
        if (!deleteItemResponse.ok) {
          throw new Error(`Failed to remove item ${item.id} from old outbound`);
        }
      }

      reloadShipmentData();
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error splitting shipment:', error);
      alert('Failed to split shipment. Please try again.');
    }
  };

  const handleFilterChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
    setFilterOptions(selectedOptions);

    // Update individual filter states
    setPrepFilter(selectedOptions.includes('prep'));
    setShelvedFilter(selectedOptions.includes('shelved'));
    setShowItemsForAllPlacements(selectedOptions.includes('allPlacements'));
  };

  const handleReassignPlan = async (outboundId, newPlanId) => {
    try {
      const newOutboundId = `${newPlanId}`;
      const response = await fetch(`${API_URL}/api/reassign_plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipmentId, outboundId, newPlanId })
      });
      if (!response.ok) {
        throw new Error('Failed to reassign plan');
      }
      const result = await response.json();
      console.log('Plan reassigned successfully:', result);
      refreshBoxes();
    } catch (error) {
      console.error('Error reassigning plan:', error);
    }
  };

  const groupAndSortPlans = (plans) => {
    const grouped = plans.reduce((acc, plan) => {
      const date = new Date(plan.created_at).toLocaleDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(plan);
      return acc;
    }, {});

    const sorted = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b));
    return sorted.map(date => ({
      date,
      plans: grouped[date].sort((a, b) => a.name.localeCompare(b.name))
    }));
  };

  const handleConfirmImport = async (updateProgress) => {
    try {
      // Step 1: Import box contents
      updateProgress('boxContents', 'Importing box contents and items...');
      
      let response;
      if (selectedPlan && selectedPlan.id) {
        // Use plan-based endpoint if plan is selected
        const replaceBoxContentsBody = { shipmentId, selectedPlan, boxes: importedData, employee: selectedNickname };
        response = await fetch(`${API_URL}/api/replace_box_contents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(replaceBoxContentsBody)
        });
      } else {
        // Use outbound-based endpoint if no plan is selected
        const replaceBoxContentsBody = { shipmentId, boxes: importedData, employee: selectedNickname };
        response = await fetch(`${API_URL}/api/replace_box_contents_outbound`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(replaceBoxContentsBody)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errorType === 'DUPLICATE_ENTRY' || errorData.errorType === 'DUPLICATE_BOXES') {
          throw new Error(errorData.error || 'Duplicate entry detected in box contents data');
        }
        throw new Error(errorData.error || 'Failed to replace box contents');
      }
      updateProgress('boxContents', `Successfully imported ${importedData.length} items`, true);

      // Step 2: Update expiry dates and services in parallel
      const itemsWithExpiry = importedData.filter(item => item.EXPIRY_DATE);
      const itemsWithServices = importedData.filter(item => item.SERVICES_ADDED);

      const parallelUpdates = [];

      // Process expiry dates
      if (itemsWithExpiry.length > 0) {
        updateProgress('expiryDates', `Updating ${itemsWithExpiry.length} expiry dates...`);
        
        const expiryPromises = itemsWithExpiry.map(async (item) => {
          const { SKU, EXPIRY_DATE } = item;
          const matchedItem = shipmentItems.find(shipmentItem => shipmentItem.item.merchant_sku === SKU);
          
          if (!matchedItem) return;

          let formattedExpiryDate;
          if (!isNaN(EXPIRY_DATE) && typeof EXPIRY_DATE === 'number') {
            formattedExpiryDate = convertExcelDateToJSDate(EXPIRY_DATE);
          } else if (!isNaN(Date.parse(EXPIRY_DATE))) {
            formattedExpiryDate = EXPIRY_DATE;
          } else {
            console.error('Invalid date format:', EXPIRY_DATE);
            return;
          }

          const updateItemPayload = {
            itemId: matchedItem.id,
            shipmentId: shipmentId,
            item: { ...matchedItem, expiry_date: formattedExpiryDate }
          };

          return fetch(`${API_URL}/api/update-expiry`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateItemPayload),
          });
        });

        parallelUpdates.push(
          Promise.all(expiryPromises).then(() => {
            updateProgress('expiryDates', `Updated ${itemsWithExpiry.length} expiry dates`, true);
          })
        );
      } else {
        updateProgress('expiryDates', 'No expiry dates to update', true);
      }

      // Process services
      if (itemsWithServices.length > 0) {
        updateProgress('services', `Applying services to ${itemsWithServices.length} items...`);
        
        const servicesPromises = itemsWithServices.map(async (item) => {
          const { SKU, SERVICES_ADDED } = item;
          const matchedItem = shipmentItems.find(shipmentItem => shipmentItem.item.merchant_sku === SKU);
          
          if (!matchedItem) return;

          const services = SERVICES_ADDED.split(',').map(s => s.trim());
          const matchedServices = servicesData.services.filter(systemService =>
            services.includes(systemService.name)
          );

          if (matchedServices.length === 0) return;

          const servicesPayload = matchedServices.reduce((acc, service, index) => {
            acc[index] = {
              id: service.id,
              name: service.name,
              charge: service.charge,
              advanced_options: service.advanced_options,
              quantity: 1,
            };
            return acc;
          }, {});

          return fetch(
            `https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}/outbound-shipment-item/${matchedItem.id}/services?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ services: servicesPayload }),
            }
          );
        });

        parallelUpdates.push(
          Promise.all(servicesPromises).then(() => {
            updateProgress('services', `Applied services to ${itemsWithServices.length} items`, true);
          })
        );
      } else {
        updateProgress('services', 'No services to apply', true);
      }

      // Wait for all parallel updates to complete
      await Promise.all(parallelUpdates);

      // Step 3: Refresh data
      updateProgress('refresh', 'Refreshing shipment data...');
      await Promise.all([
        refreshBoxes(),
        reloadShipmentData()
      ]);
      updateProgress('refresh', 'Data refreshed successfully', true);

    } catch (error) {
      console.error('Error confirming import:', error);
      throw error; // Re-throw to be handled by the modal
    }
  };

  const checkPlacementOptions = (plan) => {
    if (!plan || !plan.placement_options || plan.placement_options.length === 0) {
      console.log('No placement options available.');
      return;
    }

    const confirmedOptions = plan.placement_options.filter(option => option.confirmed_at);
    if (confirmedOptions.length === 0) {
      console.log('No placement option has been confirmed.');
      return;
    }

    const latestConfirmedOption = confirmedOptions.reduce((latest, current) => {
      return new Date(current.confirmed_at) > new Date(latest.confirmed_at) ? current : latest;
    });
    return latestConfirmedOption;
  };

  const convertExcelDateToJSDate = (serial) => {
    const excelEpoch = new Date(1899, 11, 30);
    const jsDate = new Date(excelEpoch.getTime() + (serial * 86400 * 1000));
    return jsDate.toISOString().split('T')[0];
  };

  const handleImportExcel = async (event) => {
    console.log("Uploaded file...");
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      const boxContentsSheet = workbook.Sheets['Box Contents'];
      const shipmentItemsSheet = workbook.Sheets['Shipment Items'];

      const boxContents = XLSX.utils.sheet_to_json(boxContentsSheet);
      const shipmentItems = XLSX.utils.sheet_to_json(shipmentItemsSheet);

      const combinedData = boxContents.map(boxItem => {
        const shipItem = shipmentItems.find(item => item.SKU === boxItem.SKU) || {};
        return {
          ...boxItem,
          EXPIRY_DATE: shipItem.EXPIRY_DATE || '',
          SERVICES_ADDED: shipItem.SERVICES_ADDED || '',
          FNSKU: shipItem.FNSKU || '',
          ASIN: shipItem.ASIN || '',
          LENGTH: boxItem.LENGTH || shipItem.LENGTH || '',
          WIDTH: boxItem.WIDTH || shipItem.WIDTH || '',
          HEIGHT: boxItem.HEIGHT || shipItem.HEIGHT || '',
          WEIGHT: boxItem.WEIGHT || shipItem.WEIGHT || '',
        };
      });

      // Use weight column directly as total box weight (not multiplied by quantity)
      const boxWeights = {};
      combinedData.forEach(item => {
        const boxNumber = item.BOX_NUMBER;
        const boxWeight = parseFloat(item.WEIGHT) || 0;
        
        // The weight column already contains the total box weight, not per-item weight
        if (!boxWeights[boxNumber] && boxWeight > 0) {
          boxWeights[boxNumber] = boxWeight;
        }
      });

      // Use the weight from Excel directly as the total box weight
      const updatedCombinedData = combinedData.map(item => {
        const boxNumber = item.BOX_NUMBER;
        const totalBoxWeight = boxWeights[boxNumber] || 0;
        return {
          ...item,
          // Keep original weight for reference
          ITEM_WEIGHT: item.WEIGHT, 
          // Use the Excel weight column directly as total box weight
          WEIGHT: totalBoxWeight > 0 ? totalBoxWeight.toFixed(2) : ''
        };
      });

      const boxNumbers = [...new Set(updatedCombinedData.map(item => parseInt(item.BOX_NUMBER)))];
      boxNumbers.sort((a, b) => a - b);

      for (let i = 0; i < boxNumbers.length; i++) {
        if (boxNumbers[i] !== i + 1) {
          alert(`Box numbers must be consecutive starting from 1. Missing box number: ${i + 1}`);
          return;
        }
      }

      setImportedData(updatedCombinedData);
      setShowImportModal(true);
      fileInputRef.current.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const updateItemServices = async (item, services) => {
    try {
      console.log('Updating services for item:', item);
      console.log('Services to add:', services);
      
      // Fetch available services
      const response = await fetch(
        `https://app.yyzprep.ca/api/services?type[]=outbound_shipment_item&api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.status}`);
      }
      
      const servicesData = await response.json();
      
      // Find the matching item in the shipmentItems array to get existing services
      const foundItem = shipmentItems.find(si => si.id === item.ITEM_ID);
      
      if (!foundItem) {
        console.error(`Item with ID ${item.ITEM_ID} not found in shipmentItems`);
        return;
      }
      
      console.log('Found item:', foundItem);
      
      // Get existing services
      const existingServices = foundItem.service_lines || [];
      console.log('Existing services:', existingServices);
      
      // Match new services to add
      const newServicesToAdd = servicesData.services.filter(sysService => 
        services.includes(sysService.name)
      );
      
      console.log('New services to add:', newServicesToAdd);
      
      if (newServicesToAdd.length === 0) {
        console.warn(`No matching services found for item ${item.SKU}`);
        return;
      }
      
      // Create combined services payload
      const servicesPayload = {};
      
      // Add existing services to payload
      existingServices.forEach((service, index) => {
        servicesPayload[index] = {
          id: service.service.id,
          name: service.service.name,
          charge: service.service.charge,
          advanced_options: service.service.advanced_options,
          quantity: service.quantity || 1
        };
      });
      
      // Add new services to payload
      newServicesToAdd.forEach((service, index) => {
        servicesPayload[existingServices.length + index] = {
          id: service.id,
          name: service.name,
          charge: service.charge,
          advanced_options: service.advanced_options,
          quantity: 1
        };
      });
      
      console.log('Combined services payload:', servicesPayload);
      
      // Post the combined services
      const updateResponse = await fetch(
        `https://app.yyzprep.ca/api/shipments/outbound/${item.SHIPMENT_ID}/outbound-shipment-item/${item.ITEM_ID}/services?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ services: servicesPayload }),
        }
      );
      
      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        console.error(`Failed to update services: ${updateResponse.status}`, errorText);
        throw new Error(`Failed to update services: ${updateResponse.status}`);
      }
      
      console.log(`Services updated for item ${item.SKU}`);
      
    } catch (error) {
      console.error('Error updating services:', error);
    }
  };

  useEffect(() => {
    refreshBoxes();
  }, [refreshBoxes]);

  let skuList = [];
  const [nicknames, setNicknames] = useState([]);
  const [prepFilter, setPrepFilter] = useState(false);
  const [shelvedFilter, setShelvedFilter] = useState(false);
  const [boxSortOrder, setBoxSortOrder] = useState('asc');

  // Enhanced filter states
  const [fdaFilter, setFdaFilter] = useState(null); // null, true, false
  const [priorNoticeFilter, setPriorNoticeFilter] = useState(null); // null, true, false
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showFilterHelp, setShowFilterHelp] = useState(false);

  // Add quantity sorting state
  const [quantitySortOrder, setQuantitySortOrder] = useState(null); // null, 'asc', 'desc'

  const handleInternalNotesChange = (e) => {
    setInternalNotes(e.target.value);
  };

  async function toggleLock(shipmentId) {
    try {
      const response = await fetch(
        `https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}/toggleLock?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`,
        { method: 'GET' }
      );
      const data = await response.json();
      console.log("Toggle Lock Response Data: ", data);
      return data;
    } catch (error) {
      console.error('Error toggling lock:', error);
      throw error;
    }
  }

  const handleInternalNotesBlur = async () => {
    const updatedShipmentInfo = {
      notes: shipmentInfo.notes,
      internal_notes: internalNotes
    };
    let retry = false;

    try {
      const response = await fetch(
        `https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedShipmentInfo),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update shipment info');
      }

      const data = await response.json();
      showSnackbar('Shipment info updated successfully', 'success');
      console.log("Shipment info updated successfully:", data);
    } catch (error) {
      console.error('Error updating shipment info, attempting to toggle lock:', error);

      try {
        await toggleLock(shipmentId);
        retry = true;
      } catch (lockError) {
        console.error('Error toggling lock:', lockError);
      }

      if (retry) {
        try {
          const response = await fetch(
            `https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatedShipmentInfo),
            }
          );
          if (!response.ok) {
            throw new Error('Failed to update shipment info');
          }
          const data = await response.json();
          showSnackbar('Shipment info updated successfully', 'success');
          console.log("Shipment info updated successfully after toggle lock:", data);
        } catch (retryError) {
          console.error('Error updating shipment info after toggle lock:', retryError);
        } finally {
          try {
            await toggleLock(shipmentId);
          } catch (finalLockError) {
            console.error('Error toggling lock back:', finalLockError);
          }
        }
      }
    }
  };

  const checkProgress = async () => {
    try {
      const response = await fetch(`${API_URL}/api/submit_box_progress`);
      const result = await response.json();
      setProgress(result.progress);
      if (result.progress >= 0 && result.progress < 100) {
        setTimeout(checkProgress, 1000);
      } else {
        setProgress(0);
        setShowProgressBar(false);
        setIsSubmitting(false);
      }
    } catch (error) {
      setProgress(0);
      setShowProgressBar(false);
      setIsSubmitting(false);
      console.error('Error checking progress:', error);
    }
  };

  const fetchNicknames = async () => {
    try {
      const response = await fetch(`${API_URL}/api/nicknames`);
      const data = await response.json();
      setNicknames(data);
    } catch (error) {
      console.error('Error fetching nicknames:', error);
    }
  };

  useEffect(() => {
    fetchNicknames();
  }, []);

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStatusDropdown && !event.target.closest('.status-dropdown-container')) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const handleNicknameSelect = (nickname) => {
    setSelectedNickname(nickname);
    window.selectedNickname = nickname;
  };

  useEffect(() => {
    if (selectedNickname) {
      console.log('Fetching all data...');

      // Add the recently opened update here
      const updateRecentlyOpened = async () => {
        if (!shipmentId) return;
        
        try {
          const response = await fetch(`${API_URL}/api/update_recently_opened`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              outboundId: shipmentId,
            }),
          });

          if (!response.ok) {
            console.error('Failed to update recently opened outbounds');
          }
        } catch (error) {
          console.error('Error updating recently opened outbounds:', error);
        }
      };

      updateRecentlyOpened();
    }
  }, [selectedNickname, shipmentId]);

  useEffect(() => {
  }, [boxes]);

  const findClient = (team_id, clients) => {
    const client = clients.find(client => client.id === team_id);
    if (!client) return { name: 'Unknown', country: 'Unknown', email: '' };

    const name = client.name;
    let country = 'Unknown';
    if (name.includes('(US)')) {
      country = 'USA';
    } else if (name.includes('(CA)')) {
      country = 'Canada';
    }
    return { name, country, email: client.email };
  };

  useEffect(() => {
    if (shipmentInfo && selectedPlan) {
      validateShipFromAddress(selectedPlan, clientInfo.country);
    }
  }, [shipmentInfo, selectedPlan]);

  const validateShipFromAddress = (selectedPlan, clientCountry) => {
    if (!selectedPlan || !selectedPlan.ship_from_address || !clientCountry) return;
    const shipFromCountryCode = selectedPlan.ship_from_address.country_code;

    if (
      (shipFromCountryCode === 'CA' && clientCountry === 'USA') ||
      (shipFromCountryCode === 'US' && clientCountry === 'Canada')
    ) {
      alert(
        `The ship-from location for this shipment is ${shipFromCountryCode}, but it looks like it's a ${clientCountry} shipment. Please double-check before continuing.`
      );
    } else {
      console.log(`ship from country code is ${shipFromCountryCode} and client country is ${clientCountry}`);
    }
  };

  // Helper function to parse and compare shelf codes
  const parseShelfCode = (code) => {
    if (!code) return { aisle: '', row: NaN, shelf: NaN, bin: '' };
    const parts = code.split('-');
    return {
      aisle: parts[0] || '',
      row: parseInt(parts[1], 10) || 0,
      shelf: parseInt(parts[2], 10) || 0,
      bin: parts[3] || ''
    };
  };

  const compareShelfCodes = (codeA, codeB) => {
    const parsedA = parseShelfCode(codeA);
    const parsedB = parseShelfCode(codeB);

    if (parsedA.aisle !== parsedB.aisle) return parsedA.aisle.localeCompare(parsedB.aisle);
    if (parsedA.row !== parsedB.row) return parsedA.row - parsedB.row;
    if (parsedA.shelf !== parsedB.shelf) return parsedA.shelf - parsedB.shelf;
    // Ensure bins are compared correctly, even if one is missing
    const binA = parsedA.bin || '';
    const binB = parsedB.bin || '';
    if (binA !== binB) return binA.localeCompare(binB);
    return 0;
  };

  const generatePickingList = async () => {
    console.log('Starting generatePickingList...');
    console.log('shipmentItems:', shipmentItems);
    
    // First, try to refresh the shelving data to ensure we have the latest
    try {
      console.log('Refreshing shelving data...');
      await refreshShelving();
      console.log('Shelving data refreshed.');
    } catch (error) {
      console.error('Error refreshing shelving data:', error);
      // Continue anyway with whatever data we have
    }
    
    // Debug: Log a sample item structure
    if (shipmentItems && shipmentItems.length > 0) {
      console.log('Sample shipment item structure:', JSON.stringify(shipmentItems[0], null, 2));
    }
    
    // Check if shelvingData exists and has data
    if (!shelvingData || !Array.isArray(shelvingData)) {
      console.error('shelvingData is not an array or is undefined:', shelvingData);
      alert('Error: Shelving data is not available. Please check console for details.');
      return;
    }
    
    console.log('shelvingData length:', shelvingData.length);
    
    // Debug: Log a sample shelving data structure
    if (shelvingData && shelvingData.length > 0) {
      console.log('Sample shelving data structure:', JSON.stringify(shelvingData[0], null, 2));
    }
    
    const pickingInstructions = [];

    shipmentItems.forEach(shipmentItem => {
      console.log('Processing shipment item:', shipmentItem);
      
      // Check if item has the required properties
      if (!shipmentItem || !shipmentItem.item || !shipmentItem.item.merchant_sku) {
        console.error('Missing required properties on shipment item:', shipmentItem);
        return; // Skip this item
      }
      
      let remainingQuantityNeeded = shipmentItem.quantity;
      if (remainingQuantityNeeded <= 0) {
        console.log('Skipping item with zero or negative quantity:', shipmentItem);
        return; // Skip if quantity is 0 or less
      }

      // Log all SKUs in shelvingData for debugging
      console.log('All SKUs in shelvingData:', shelvingData.map(d => d.SKU));
      
      const relevantShelves = shelvingData.filter(
        data => {
          const isMatch = data.SKU === shipmentItem.item.merchant_sku;
          const hasQuantity = parseInt(data.QTY, 10) > 0;
          
          console.log(`Checking shelf for ${shipmentItem.item.merchant_sku}:`, {
            shelf: data.FULL_SHELF_CODE,
            shelfSKU: data.SKU,
            quantity: data.QTY,
            isMatch,
            hasQuantity
          });
          
          return isMatch && hasQuantity;
        }
      );
      
      console.log('Item SKU:', shipmentItem.item.merchant_sku);
      console.log('Relevant shelves found:', relevantShelves.length);
      console.log('Relevant shelves:', relevantShelves);

      if (relevantShelves.length > 0) {
        const sortedShelves = [...relevantShelves].sort((a, b) =>
          compareShelfCodes(a.FULL_SHELF_CODE, b.FULL_SHELF_CODE)
        );

        for (const shelf of sortedShelves) {
          // Check if shelf has required properties
          if (!shelf || !shelf.FULL_SHELF_CODE || !shelf.QTY) {
            console.error('Shelf missing required properties:', shelf);
            continue; // Skip this shelf
          }
          
          const quantityOnShelf = parseInt(shelf.QTY, 10) || 0;
          const quantityToPick = Math.min(remainingQuantityNeeded, quantityOnShelf);

          console.log('Shelf:', shelf.FULL_SHELF_CODE, 'Quantity on shelf:', quantityOnShelf, 'Quantity to pick:', quantityToPick);

          if (quantityToPick > 0) {
            pickingInstructions.push({
              title: shipmentItem.item.title || 'Unknown Item',
              sku: shipmentItem.item.merchant_sku, // Include SKU for clarity
              shelvingCode: shelf.FULL_SHELF_CODE,
              quantityToPick: quantityToPick,
              originalShipmentQty: shipmentItem.quantity // Add original qty for reference
            });
            remainingQuantityNeeded -= quantityToPick;
          }

          if (remainingQuantityNeeded <= 0) {
            break; // Got enough for this shipment item
          }
        }
      } else {
        console.log('No relevant shelves found for SKU:', shipmentItem.item.merchant_sku);
      }
    });

    // Sort the final list globally by shelving code
    pickingInstructions.sort((a, b) => compareShelfCodes(a.shelvingCode, b.shelvingCode));
    
    console.log('Final picking instructions:', pickingInstructions);
    console.log('Picking instructions length:', pickingInstructions.length);

    // Only show modal if we have instructions
    if (pickingInstructions.length === 0) {
      alert('No picking instructions generated. Either there are no shelved items or the quantities on shelves are zero.');
    }
    
    setPickingListData(pickingInstructions);
    setShowPickingListModal(true);
  };

  const handleUpdateBoxDetails = async (updatedBox) => {
    try {
      // Use outbound-based endpoint that doesn't require plan selection
      const response = await fetch(`${API_URL}/api/update_box_details_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          shipmentId, 
          boxNumber: updatedBox.boxNumber,
          length: updatedBox.length, 
          width: updatedBox.width, 
          height: updatedBox.height, 
          weight: updatedBox.weight, 
          selectedNickname 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update box details');
      }
      
      await refreshBoxes();
      
      // Force a small delay and refresh again to ensure data is fully synced
      setTimeout(async () => {
        await refreshBoxes();
      }, 500);
    } catch (error) {
      console.error('Error updating box details:', error);
    }
  };

  const handleBoxClick = (box) => {
    const newBoxNumber = openBoxNumber === box.boxNumber ? null : box.boxNumber;
    const newSelectedBox = openBoxNumber === box.boxNumber ? null : box;

    console.log('handleBoxClick: clicked box', box.boxNumber, 'newBoxNumber =', newBoxNumber);

    setOpenBoxNumber(newBoxNumber);
    setSelectedBox(newSelectedBox);

    // Update refs immediately for Book Mode
    openBoxNumberRef.current = newBoxNumber;
    selectedBoxRef.current = newSelectedBox;
  };

  const handleAddBox = async () => {
    const newBoxNumber = boxes.length + 1;
    const newBox = {
      boxNumber: newBoxNumber,
      items: [],
      LENGTH: null,
      WIDTH: null,
      HEIGHT: null,
      WEIGHT: null,
      OUTBOUND_ID: shipmentId,
      employee: selectedNickname
    };
    try {
      // Use outbound-based endpoint that doesn't require plan selection
      const response = await fetch(`${API_URL}/api/add_box_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBox)
      });
      const result = await response.json();
      if (result.success) {
        refreshBoxes();
      } else {
        console.error('Error adding box:', result.error);
      }
    } catch (error) {
      console.error('Error adding box:', error);
    }
  };

  const filteredDbData = boxes
    .filter(box =>
      (box.items.length === 0 ||
        box.items.some(item =>
          item.SKU.toLowerCase().includes(boxSearchTerm.toLowerCase()) ||
          item.ASIN.toLowerCase().includes(boxSearchTerm.toLowerCase()) ||
          item.FNSKU.toLowerCase().includes(boxSearchTerm.toLowerCase())
        )) &&
      (!filterByPackingGroup ||
        box.items.length === 0 ||
        (selectedPackingGroup?.items &&
          box.items.some(boxItem =>
            selectedPackingGroup.items.some(packingItem => packingItem.msku === boxItem.SKU)
          )))
    )
    .sort((a, b) =>
      boxSortOrder === 'asc'
        ? a.boxNumber.localeCompare(b.boxNumber)
        : b.boxNumber.localeCompare(a.boxNumber)
    );

  const sortedDbData = [...filteredDbData].sort((a, b) => {
    if (boxSortOrder === 'asc') {
      return a.boxNumber - b.boxNumber;
    } else {
      return b.boxNumber - a.boxNumber;
    }
  });

  const handleDuplicateBox = async (box, times = 1) => {
    try {
      // Use outbound-based endpoint that doesn't require plan selection
      const response = await fetch(`${API_URL}/api/duplicate_box_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outbound_id: shipmentId,
          box,
          maxBoxLength: boxes.length,
          times,
          employee: selectedNickname
        })
      });
      console.log("Sending dupe info: ", { outbound_id: shipmentId, box, maxBoxLength: boxes.length, times, employee: selectedNickname });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to duplicate box: ${errorText}`);
      }
      await refreshBoxes();
    } catch (error) {
      console.error('Error duplicating box:', error);
    }
  };

  const calculateTotalQuantityInBoxes = (sku, boxes) => {
    if (!Array.isArray(boxes)) {
      return 0;
    }
    return boxes.reduce((acc, box) => {
      const matchingItems = box.items.filter(item => item.SKU === sku);
      const totalQty = matchingItems.reduce((itemAcc, item) => itemAcc + Number(item.QTY || 0), 0);
      return acc + totalQty;
    }, 0);
  };

  // Calculate remaining quantities for items not fully boxed
  const calculateRemainingQuantities = () => {
    const remainingItems = [];
    
    // Use selectedPlan if available, otherwise use the most recent plan
    const planToUse = selectedPlan || (plans?.length > 0 
      ? plans.reduce((latest, current) => 
          new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        )
      : null);
    
    if (!planToUse) return remainingItems;
    
    // Filter items to only include those from the plan being used
    const itemsToCheck = shipmentItems.filter(item => 
      planToUse.items.some(planItem => planItem.msku === item.item.merchant_sku)
    );
    
    itemsToCheck.forEach(item => {
      const planItem = planToUse.items.find(planItem => planItem.msku === item.item.merchant_sku);
      const totalQuantity = planItem?.quantity || item.quantity;
      const quantityInBoxes = quantitiesInBoxes[item.item.merchant_sku] || 0;
      const remainingQuantity = totalQuantity - quantityInBoxes;
      
      if (remainingQuantity > 0) {
        remainingItems.push({
          ...item,
          totalQuantity,
          quantityInBoxes,
          remainingQuantity,
          planItem
        });
      }
    });
    
    return remainingItems;
  };

  // Create a dependency that changes whenever box contents change
  const boxItemsSignature = JSON.stringify(boxes?.map(b => ({ 
    boxNumber: b.boxNumber,
    items: b.items.map(i => `${i.SKU}:${i.QTY}`)
  })));

  const quantitiesInBoxes = useMemo(() => {
    const quantities = {};
    
    // Use selectedPlan if available, otherwise use the most recent plan
    const planToUse = selectedPlan || (plans?.length > 0 
      ? plans.reduce((latest, current) => 
          new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        )
      : null);
    
    // Filter items to only include those from the plan being used
    const itemsToCount = planToUse 
      ? shipmentItems.filter(item => 
          planToUse.items.some(planItem => planItem.msku === item.item.merchant_sku)
        )
      : shipmentItems;
    
    itemsToCount.forEach(item => {
      const sku = item.item.merchant_sku;
      let count = 0;
      
      (boxes || []).forEach(box => {
        (box.items || []).forEach(boxItem => {
          // ONLY match by SKU - no ASIN fallback
          if (boxItem.SKU === sku) {
            count += Number(boxItem.QTY || 0);
          }
        });
      });
      
      quantities[sku] = count;
    });
    
    return quantities;
  }, [shipmentItems, boxes, boxItemsSignature, selectedPlan, plans]);

  const getItemStatus = (item) => {
    const totalQuantity = Number(item.quantity);
    const quantityInBoxes = quantitiesInBoxes[item.item.merchant_sku] || 0;

    if (quantityInBoxes <= 0) return 'Not Started';
    if (quantityInBoxes < totalQuantity) return 'In Progress';
    if (quantityInBoxes === totalQuantity) return 'Completed';
    return 'Over Limit';
  };

  const filterItemsByStatus = (items, statusArray) => {
    if (statusArray.includes('All') || statusArray.length === 0) return items;
    return items.filter(item => statusArray.includes(getItemStatus(item)));
  };

  const filterItemsByPrep = (items, prepFilter) => {
    if (!prepFilter) return items;
    return items.filter(item => {
      const prepRequirement = prepRequirements[item.item.merchant_sku];
      return prepRequirement && prepRequirement.length > 0 && !prepRequirement.includes('N/A');
    });
  };

  const filterItemsByShelved = (items, shelvedFilter) => {
    if (!shelvedFilter) return items;
    return items.filter(item => shelvingData.some(data => data.SKU === item.item.merchant_sku));
  };

  // Enhanced filtering functions
  const filterItemsByFDA = (items, fdaFilter) => {
    if (fdaFilter === null) return items;
    return items.filter(item => {
      // Use actual FDA classification data from context
      const classification = fdaClassifications[item?.item?.asin];
      let hasFDA = false;
      
      if (classification) {
        // Use actual classification data
        hasFDA = classification.fdaLikely || false;
      } else {
        // Fallback to keyword heuristic only if no classification data available
        hasFDA = item.item.title && (
          item.item.title.toLowerCase().includes('food') ||
          item.item.title.toLowerCase().includes('supplement') ||
          item.item.title.toLowerCase().includes('vitamin') ||
          item.item.title.toLowerCase().includes('cosmetic')
        ) || false;
      }
      
      return fdaFilter ? hasFDA : !hasFDA;
    });
  };

  const filterItemsByPriorNotice = (items, priorNoticeFilter) => {
    if (priorNoticeFilter === null) return items;
    return items.filter(item => {
      // Use actual Prior Notice classification data from context
      const classification = fdaClassifications[item?.item?.asin];
      let requiresPriorNotice = false;
      
      if (classification) {
        // Use actual classification data
        requiresPriorNotice = classification.priorNoticeRequired || false;
      } else {
        // Fallback to keyword heuristic only if no classification data available
        requiresPriorNotice = item.item.title && (
          item.item.title.toLowerCase().includes('food') ||
          item.item.title.toLowerCase().includes('beverage') ||
          item.item.title.toLowerCase().includes('supplement') ||
          item.item.title.toLowerCase().includes('tea') ||
          item.item.title.toLowerCase().includes('coffee')
        ) || false;
      }
      
      return priorNoticeFilter ? requiresPriorNotice : !requiresPriorNotice;
    });
  };

  // Comprehensive filtering with preset combinations


  // Combined filtering function
  const applyAllFilters = (items) => {
    let filteredItems = items;

    // Apply individual filters
    if (prepFilter) {
      filteredItems = filterItemsByPrep(filteredItems, prepFilter);
    }
    if (shelvedFilter) {
      filteredItems = filterItemsByShelved(filteredItems, shelvedFilter);
    }
    if (fdaFilter !== null) {
      filteredItems = filterItemsByFDA(filteredItems, fdaFilter);
    }
    if (priorNoticeFilter !== null) {
      filteredItems = filterItemsByPriorNotice(filteredItems, priorNoticeFilter);
    }

    // Vision detection filter - filter to items detected by camera
    if (visionDetectedItems.length > 0) {
      filteredItems = filteredItems.filter(item =>
        visionDetectedItems.includes(item.item?.asin) ||
        visionDetectedItems.includes(item.item?.fnsku) ||
        visionDetectedItems.includes(item.item?.merchant_sku)
      );
    }

    return filteredItems;
  };

  // Reset all filters
  const resetAllFilters = () => {
    setPrepFilter(false);
    setShelvedFilter(false);
    setFdaFilter(null);
    setPriorNoticeFilter(null);
    setSelectedPackingGroupFilter(null);
    setStatusFilter(['All']);
    setVisionDetectedItems([]);
  };

  const filterItemsByPackingGroup = (items, selectedPackingGroup, showItemsForAllPlacements) => {
    if (showItemsForAllPlacements || !selectedPackingGroup) return items;
    console.log("Selected packing group:", selectedPackingGroup);
    
    // First, get the plan items map from the selected plan
    const planItemsMap = new Map(
        selectedPlan.items.map(item => [item.id, item.msku])
    );
    
    // Create a set of MSKUs from the packing group's plan item IDs
    const packingGroupMskus = new Set(
        selectedPackingGroup.items
            .map(item => planItemsMap.get(item.planItemId))
            .filter(Boolean) // Remove any undefined values
    );
    
    console.log("Packing group MSKUs:", [...packingGroupMskus]);
    
    // Filter items using the MSKUs from the plan items
    return items.filter(item => packingGroupMskus.has(item.item.merchant_sku));
  };

  useEffect(() => {
    console.log('Modal state changed:', showSendEmailModal);
  }, [showSendEmailModal]);

  useEffect(() => {
    // Filter items by selected plan first, then by packing group
    let itemsForProgress = shipmentItems;
    
    // Use selectedPlan if available, otherwise use the most recent plan
    const planToUseForProgress = selectedPlan || (plans?.length > 0 
      ? plans.reduce((latest, current) => 
          new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        )
      : null);
    
    if (planToUseForProgress) {
      itemsForProgress = shipmentItems
        .filter(item => planToUseForProgress.items.some(planItem => planItem.msku === item.item.merchant_sku))
        .map(item => {
          const planItem = planToUseForProgress.items.find(planItem => planItem.msku === item.item.merchant_sku);
          return { ...item, quantity: planItem.quantity };
        });
    }

    // Then apply packing group filter
    const filteredItems = filterItemsByPackingGroup(
      itemsForProgress,
      selectedPackingGroup,
      showItemsForAllPlacements
    );

    if (filteredItems && boxes) {
      const totalUnitsValue = filteredItems.reduce((acc, item) => acc + item.quantity, 0);
      const unitsPackedValue = filteredItems.reduce(
        (acc, item) => acc + (quantitiesInBoxes[item.item.merchant_sku] || 0),
        0
      );
      const totalSKUsValue = filteredItems.length;
      const skusCompletedValue = filteredItems.filter(
        item => (quantitiesInBoxes[item.item.merchant_sku] || 0) >= item.quantity
      ).length;

      setTotalUnits(totalUnitsValue);
      setUnitsPacked(unitsPackedValue);
      setSkusCompleted(skusCompletedValue);
      setTotalSKUs(totalSKUsValue);

      const unitProgressValue =
        totalUnitsValue > 0 ? (unitsPackedValue / totalUnitsValue) * 100 : 0;
      setUnitProgress(unitProgressValue);

      const skuProgressValue = totalSKUsValue > 0 ? (skusCompletedValue / totalSKUsValue) * 100 : 0;
      setSkuProgress(skuProgressValue);
    }
  }, [shipmentItems, boxes, selectedPackingGroup, showItemsForAllPlacements, quantitiesInBoxes, selectedPlan, plans]);

  const downloadUPSLabels = () => {
    // Use selectedPlan if available, otherwise fall back to most recent plan
    const planToUse = selectedPlan || (plans?.length > 0 
      ? plans.reduce((latest, current) => 
          new Date(current.created_at) > new Date(latest.created_at) ? current : latest
        )
      : null);
    
    if (!planToUse) {
      alert("No plans available for downloading UPS labels.");
      return;
    }
    
    const placementOption = checkPlacementOptions(planToUse);
    if (!placementOption) {
      alert("No confirmed placement options available for downloading UPS labels.");
      return;
    }
    
    // Add progress callback to show status updates
    const progressCallback = (message) => {
      console.log(`UPS Label Progress: ${message}`);
    };
    
    fetchUpsMergedLabels(placementOption, planToUse.id, progressCallback, shipmentId);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const flattenedData = [];

    boxes.forEach(box => {
      box.items.forEach(item => {
        flattenedData.push({
          BOX_NUMBER: box.boxNumber,
          TITLE: item.TITLE,
          SKU: item.SKU,
          QTY: item.QTY,
          LENGTH: item.LENGTH,
          WIDTH: item.WIDTH,
          HEIGHT: item.HEIGHT,
          WEIGHT: box.weight
        });
      });
    });

    const wsBoxes = XLSX.utils.json_to_sheet(flattenedData);
    XLSX.utils.book_append_sheet(wb, wsBoxes, 'Box Contents');

    const shipmentItemsData = shipmentItems.map(item => ({
      TITLE: item.item.title,
      SKU: item.item.merchant_sku,
      ASIN: item.item.asin,
      FNSKU: item.item.fnsku,
      EXPIRY_DATE: item.expiry_date || '',
      SERVICES_ADDED: item.service_lines.map(serviceLine => serviceLine.service.name).join(', ')
    }));
    const wsShipmentItems = XLSX.utils.json_to_sheet(shipmentItemsData);
    XLSX.utils.book_append_sheet(wb, wsShipmentItems, 'Shipment Items');

    XLSX.writeFile(wb, `${shipmentId}_Export.xlsx`);
  };

  // Handler functions for oversized fee actions (using YYZPrep API like OutboundItem.jsx)
  const handleAddOversized = async (item) => {
    const itemKey = `${item.id}-oversized`;
    
    try {
      // Set loading state
      setLoadingServices(prev => ({ ...prev, [itemKey]: true }));
      
      console.log('Adding oversized fee for item:', item.item.merchant_sku);
      
      // Check if servicesData is available
      if (!servicesData || !servicesData.services) {
        throw new Error('Services data not available. Please refresh the page and try again.');
      }
      
      // Find the "Amazon Oversize Fee" service from servicesData
      const oversizeService = servicesData.services.find(service => 
        service.name.toLowerCase().includes('oversize') && 
        !service.name.toLowerCase().includes('huge')
      );
      
      if (!oversizeService) {
        throw new Error('Amazon Oversize Fee service not found in available services');
      }
      
      // Create updated services payload including existing services + new oversize fee
      const currentServices = item.service_lines || [];
      const updatedServices = [...currentServices];
      
      // Add the oversize service if not already present
      const hasOversizeService = currentServices.some(sl => sl.service.id === oversizeService.id);
      if (!hasOversizeService) {
        updatedServices.push({
          service: oversizeService,
          shipment_item_id: item.id
        });
      }
      
      // Prepare services payload in the format expected by YYZPrep API
      const servicesPayload = updatedServices.reduce((acc, serviceLine, index) => {
        acc[index] = {
          id: serviceLine.service.id,
          name: serviceLine.service.name,
          charge: serviceLine.service.charge,
          advanced_options: serviceLine.service.advanced_options,
          quantity: 1
        };
        return acc;
      }, {});
      
      const response = await fetch(`https://app.yyzprep.ca/api/shipments/outbound/${item.shipment_id}/outbound-shipment-item/${item.id}/services?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          services: servicesPayload
        }),
      });
      
      if (!response.ok) throw new Error('Failed to add oversized fee');
      
      console.log('Oversized fee added successfully');
      
      // Show success state
      setCompletedServices(prev => ({ ...prev, [itemKey]: true }));
      
      // Refresh shipment data 
      await reloadShipmentData();
      
      // Refresh the oversized items list to update the buttons (keep modal open for user to continue)
      setTimeout(() => {
        checkOversizedItemsWithoutFees(); // Just refresh the list, don't auto-close modal
      }, 1500); // Increased delay to allow API data to settle
      
    } catch (error) {
      console.error('Error adding oversized fee:', error);
      alert('Failed to add oversized fee. Please try again.');
    } finally {
      // Remove loading state
      setLoadingServices(prev => {
        const newState = { ...prev };
        delete newState[itemKey];
        return newState;
      });
    }
  };
  
  const handleAddHugeOversized = async (item) => {
    const itemKey = `${item.id}-huge`;
    
    try {
      // Set loading state
      setLoadingServices(prev => ({ ...prev, [itemKey]: true }));
      
      console.log('Adding huge oversized fee for item:', item.item.merchant_sku);
      
      // Check if servicesData is available
      if (!servicesData || !servicesData.services) {
        throw new Error('Services data not available. Please refresh the page and try again.');
      }
      
      // Find the "Amazon Huge Oversize Fee" service from servicesData
      const hugeOversizeService = servicesData.services.find(service => 
        service.name.toLowerCase().includes('huge') && 
        service.name.toLowerCase().includes('oversize')
      );
      
      if (!hugeOversizeService) {
        throw new Error('Amazon Huge Oversize Fee service not found in available services');
      }
      
      // Create updated services payload including existing services + new huge oversize fee
      const currentServices = item.service_lines || [];
      const updatedServices = [...currentServices];
      
      // Add the huge oversize service if not already present
      const hasHugeOversizeService = currentServices.some(sl => sl.service.id === hugeOversizeService.id);
      if (!hasHugeOversizeService) {
        updatedServices.push({
          service: hugeOversizeService,
          shipment_item_id: item.id
        });
      }
      
      // Prepare services payload in the format expected by YYZPrep API
      const servicesPayload = updatedServices.reduce((acc, serviceLine, index) => {
        acc[index] = {
          id: serviceLine.service.id,
          name: serviceLine.service.name,
          charge: serviceLine.service.charge,
          advanced_options: serviceLine.service.advanced_options,
          quantity: 1
        };
        return acc;
      }, {});
      
      const response = await fetch(`https://app.yyzprep.ca/api/shipments/outbound/${item.shipment_id}/outbound-shipment-item/${item.id}/services?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          services: servicesPayload
        }),
      });
      
      if (!response.ok) throw new Error('Failed to add huge oversized fee');
      
      console.log('Huge oversized fee added successfully');
      
      // Show success state
      setCompletedServices(prev => ({ ...prev, [itemKey]: true }));
      
      // Refresh shipment data 
      await reloadShipmentData();
      
      // Refresh the oversized items list to update the buttons (keep modal open for user to continue)
      setTimeout(() => {
        checkOversizedItemsWithoutFees(); // Just refresh the list, don't auto-close modal
      }, 1500); // Increased delay to allow API data to settle
      
    } catch (error) {
      console.error('Error adding huge oversized fee:', error);
      alert('Failed to add huge oversized fee. Please try again.');
    } finally {
      // Remove loading state
      setLoadingServices(prev => {
        const newState = { ...prev };
        delete newState[itemKey];
        return newState;
      });
    }
  };
  
  const handleSwapToHugeOversized = async (item) => {
    const itemKey = `${item.id}-swap`;
    
    try {
      // Set loading state
      setLoadingServices(prev => ({ ...prev, [itemKey]: true }));
      
      console.log('Swapping to huge oversized fee for item:', item.item.merchant_sku);
      
      // Check if servicesData is available
      if (!servicesData || !servicesData.services) {
        throw new Error('Services data not available. Please refresh the page and try again.');
      }
      
      // Find the "Amazon Huge Oversize Fee" service from servicesData
      const hugeOversizeService = servicesData.services.find(service => 
        service.name.toLowerCase().includes('huge') && 
        service.name.toLowerCase().includes('oversize')
      );
      
      if (!hugeOversizeService) {
        throw new Error('Amazon Huge Oversize Fee service not found in available services');
      }
      
      // Create updated services payload: remove existing oversized services and add huge oversize
      const currentServices = item.service_lines || [];
      const filteredServices = currentServices.filter(sl => 
        !(sl.service.name.toLowerCase().includes('oversized') || 
          sl.service.name.toLowerCase().includes('oversize'))
      );
      
      // Add the huge oversize service
      filteredServices.push({
        service: hugeOversizeService,
        shipment_item_id: item.id
      });
      
      // Prepare services payload in the format expected by YYZPrep API
      const servicesPayload = filteredServices.reduce((acc, serviceLine, index) => {
        acc[index] = {
          id: serviceLine.service.id,
          name: serviceLine.service.name,
          charge: serviceLine.service.charge,
          advanced_options: serviceLine.service.advanced_options,
          quantity: 1
        };
        return acc;
      }, {});
      
      const response = await fetch(`https://app.yyzprep.ca/api/shipments/outbound/${item.shipment_id}/outbound-shipment-item/${item.id}/services?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          services: servicesPayload
        }),
      });
      
      if (!response.ok) throw new Error('Failed to swap to huge oversized fee');
      
      console.log('Successfully swapped to huge oversized fee');
      
      // Show success state
      setCompletedServices(prev => ({ ...prev, [itemKey]: true }));
      
      // Refresh shipment data 
      await reloadShipmentData();
      
      // Refresh the oversized items list to update the buttons (keep modal open for user to continue)
      setTimeout(() => {
        checkOversizedItemsWithoutFees(); // Just refresh the list, don't auto-close modal
      }, 1500); // Increased delay to allow API data to settle
      
    } catch (error) {
      console.error('Error swapping to huge oversized fee:', error);
      alert('Failed to swap to huge oversized fee. Please try again.');
    } finally {
      // Remove loading state
      setLoadingServices(prev => {
        const newState = { ...prev };
        delete newState[itemKey];
        return newState;
      });
    }
  };

    const handleSubmitBoxContents = async (confirmed = false, ignoreOversizedCheck = false, planToUse = null, forceRefresh = false) => {
    console.log('handleSubmitBoxContents called with:', {
      confirmed,
      ignoreOversizedCheck,
      planToUse: planToUse?.id,
      forceRefresh,
      isValidationConfirmed,
      currentSubmissionPlan: currentSubmissionPlan?.id
    });
    
    // NEW: Check for validation errors FIRST, before plan selection
    if (!confirmed && !isValidationConfirmed) {
      console.log('Checking validation errors...');
      const allValidationErrors = getAllBoxValidationErrors();
      
      if (allValidationErrors.length > 0) {
        console.log('Validation errors found, showing modal');
        setValidationErrors(allValidationErrors);
        setShowValidationErrorsModal(true);
        return;
      }
    }

    // Use the passed plan, current submission plan, or the currently selected plan
    const currentPlan = planToUse || currentSubmissionPlan || selectedPlan;
    
    // Validate plan country against client country
    if (currentPlan && currentPlan.ship_from_address && clientInfo.country) {
      const planCountry = currentPlan.ship_from_address.country_code;
      const clientCountryLabel = clientInfo.country; // 'USA' or 'Canada'
      
      const expectedPlanCountry = clientCountryLabel === 'USA' ? 'US' : 'CA';

      if (planCountry !== expectedPlanCountry) {
        alert(
          `CRITICAL ERROR: Plan country mismatch. This is a ${clientCountryLabel} client, so the plan's ship-from country must be ${expectedPlanCountry}. This plan is set to ${planCountry}.`
        );
        return; // Stop the submission
      }
    }
    
    console.log('Current plan check:', {
      planToUse: planToUse?.id,
      currentSubmissionPlan: currentSubmissionPlan?.id,
      selectedPlan: selectedPlan?.id,
      currentPlan: currentPlan?.id
    });
    
    // Set the current submission plan if we have one
    if (currentPlan && !currentSubmissionPlan) {
      setCurrentSubmissionPlan(currentPlan);
    }
    
    // Check if plan is selected - if not, show plan selection modal
    if (!currentPlan) {
      console.log('No plan selected, showing plan selection modal');
      setShowPlanSelectionModal(true);
      return;
    }
    
    const missingDimensions = validateBoxDimensions(boxes);
    if (missingDimensions.length > 0) {
      alert(`Please fix the following missing dimensions:\n${missingDimensions.join('\n')}`);
      return;
    }
    
    // Force refresh shipment data before checking oversized items if requested
    if (forceRefresh) {
      console.log("Force refreshing shipment data before oversized check...");
      try {
        await reloadShipmentData();
        console.log("Shipment data refreshed successfully");
      } catch (error) {
        console.error("Failed to refresh shipment data:", error);
      }
    }
    
    if (!ignoreOversizedCheck) {
      console.log('Checking oversized items...');
      if (!checkOversizedItemsWithoutFees()) {
        console.log('Oversized items check failed, showing modal');
        return;
      }
      console.log('Oversized items check passed');
    } else {
      console.log('Skipping oversized items check');
    }
    if (!confirmed) {
      setShowShelfReminder(true);
      return;
    }

    const transformedBoxes = [];
    let currentBox = null;
    let repeatCount = 1;

    const latestPackingOption = currentPlan.packing_options.reduce((latest, current) => {
      return !latest || new Date(current.confirmed_at) > new Date(latest.confirmed_at)
        ? current
        : latest;
    }, null);

    const packingGroups = latestPackingOption?.packing_groups || [];
    const findPackingGroupId = (boxItems, packingGroups) => {
      console.log("Box items:", boxItems);
      console.log("Packing groups:", packingGroups);
      for (const packingGroup of packingGroups) {
        // Find the corresponding plan items for this packing group
        const packingGroupPlanItemIds = new Set(packingGroup.items.map(packingItem => packingItem.planItemId));
        // Match box items' SKU with plan item IDs
        const allItemsMatch = boxItems.every(boxItem => {
          // Find the plan item that matches this box item's SKU
          const planItem = currentPlan.items.find(planItem => planItem.msku === boxItem.SKU);
          // Check if this plan item's ID is in the packing group
          return planItem && packingGroupPlanItemIds.has(planItem.id);
        });
        if (allItemsMatch) {
          return packingGroup.id;
        }
      }
      return null;
    };

    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];
      let packing_group_id = null;

      if (latestPackingOption && latestPackingOption.packing_groups) {
        // If there's only one packing group, use that
        if (latestPackingOption.packing_groups.length === 1) {
          packing_group_id = latestPackingOption.packing_groups[0].id;
        } else {
          // Otherwise check for matching items
          console.log("Checking packing groups:", latestPackingOption.packing_groups);
          packing_group_id = findPackingGroupId(box.items, latestPackingOption.packing_groups);
        }
      }
      console.log("Packing group ID:", packing_group_id); 
      
      // Get box dimensions from the box object itself, not from items
      const rawWeight = parseFloat(box.weight);
      const rawLength = parseFloat(box.length);
      const rawWidth = parseFloat(box.width);
      const rawHeight = parseFloat(box.height);
      
      // Validate that all dimensions are valid and non-zero
      if (!rawWeight || rawWeight <= 0) {
        throw new Error(`Box ${i + 1} has invalid weight: ${box.weight}kg. Please fix the box dimensions before submitting.`);
      }
      if (!rawLength || rawLength <= 0) {
        throw new Error(`Box ${i + 1} has invalid length: ${box.length}cm. Please fix the box dimensions before submitting.`);
      }
      if (!rawWidth || rawWidth <= 0) {
        throw new Error(`Box ${i + 1} has invalid width: ${box.width}cm. Please fix the box dimensions before submitting.`);
      }
      if (!rawHeight || rawHeight <= 0) {
        throw new Error(`Box ${i + 1} has invalid height: ${box.height}cm. Please fix the box dimensions before submitting.`);
      }
      
      const transformedBox = {
        length_mm: Math.round(rawLength * 10), // Convert cm to mm
        width_mm: Math.round(rawWidth * 10),   // Convert cm to mm
        height_mm: Math.round(rawHeight * 10), // Convert cm to mm
        weight_gm: Math.round(rawWeight * 1000), // Convert kg to grams
        repeat: 1,
        packing_group_id: packing_group_id,
        items: box.items.map(item => {
          const matchingItem = shipmentItems.find(
            shipmentItem => shipmentItem.item.merchant_sku === item.SKU
          );
          const planItem = currentPlan.items.find(planItem => planItem.msku === item.SKU);
          return {
            item_id: matchingItem ? matchingItem.item.id : null,
            quantity: item.QTY,
            plan_item_id: planItem ? planItem.id : null,
            expiration: matchingItem ? matchingItem.expiry_date : null
          };
        })
      };

      if (currentBox && JSON.stringify(currentBox) === JSON.stringify(transformedBox)) {
        repeatCount++;
      } else {
        if (currentBox) {
          currentBox.repeat = repeatCount;
          transformedBoxes.push(currentBox);
        }
        currentBox = transformedBox;
        repeatCount = 1;
      }
    }

    if (currentBox) {
      currentBox.repeat = repeatCount;
      transformedBoxes.push(currentBox);
    }

    console.log("Transformed boxes we're submitting:");
    console.log(transformedBoxes);

    console.log('=== STARTING ACTUAL SUBMISSION ===');
    console.log('Final plan being used:', currentPlan?.id);
    console.log('Transformed boxes count:', transformedBoxes.length);
    
    setShowProgressBar(true);
    setIsSubmitting(true);

    await fetch(`${API_URL}/api/begin_box_progress`);
    checkProgress();

    try {
      // Use the original submission endpoint since we now have the selected plan
      const response = await fetch(`${API_URL}/api/submit_box_contents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipmentId, selectedPlan: currentPlan, boxes: transformedBoxes })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message);
      }

      window.open(`https://app.yyzprep.ca/staff/fba-transport/v2024/plans/${currentPlan.id}#box_contents`,'_blank');
    } catch (error) {
      console.error('Error submitting box contents:', error);
      setIsSubmitting(false);
      alert(`Error submitting box contents: ${error.message}`);
    }
  };

  // Book Mode: Find item by scanned barcode (UPC/EAN/ISBN/SKU/ASIN)
  const findItemByBarcode = useCallback((scannedCode) => {
    if (!shipmentItems || shipmentItems.length === 0) {
      console.log('Book Mode: No shipment items available');
      return [];
    }

    const normalizedCode = scannedCode.trim().replace(/-/g, '');
    const upperCode = normalizedCode.toUpperCase();

    console.log('Book Mode: Searching for barcode:', normalizedCode);
    console.log('Book Mode: Total shipment items:', shipmentItems.length);

    // Helper function to search with a specific code
    const searchWithCode = (codeToSearch) => {
      const upperSearch = codeToSearch.toUpperCase();
      return shipmentItems.filter(si => {
        const identifiers = si.item?.identifiers || [];

        // Check identifiers (UPC, EAN, ISBN)
        const identifierMatch = identifiers.some(id => {
          const type = id.identifier_type;
          const value = id.identifier?.replace(/-/g, '');

          // Match UPC, EAN, or ISBN (case-insensitive comparison)
          if (['UPC', 'EAN', 'ISBN'].includes(type)) {
            const matches = value === codeToSearch || value?.toUpperCase() === upperSearch;
            if (matches) {
              console.log(`Book Mode: Found ${type} match:`, value);
            }
            return matches;
          }
          return false;
        });

        if (identifierMatch) return true;

        // Fallback: Also check if scanned code matches SKU or ASIN directly
        const skuMatch = si.item?.merchant_sku?.toUpperCase() === upperSearch;
        const asinMatch = si.item?.asin?.toUpperCase() === upperSearch;

        if (skuMatch) console.log('Book Mode: Found SKU match:', si.item?.merchant_sku);
        if (asinMatch) console.log('Book Mode: Found ASIN match:', si.item?.asin);

        return skuMatch || asinMatch;
      });
    };

    // First try: exact match
    let matchingItems = searchWithCode(normalizedCode);
    console.log('Book Mode: Exact match found', matchingItems.length, 'items');

    // If no match and code is long enough, try ISBN variations
    if (matchingItems.length === 0 && normalizedCode.length >= 10) {
      // Try dropping the leading digit (ISBN-13 to ISBN-10 style, or check digit variation)
      const withoutLeading = normalizedCode.substring(1);
      console.log('Book Mode: Trying without leading digit:', withoutLeading);
      matchingItems = searchWithCode(withoutLeading);

      if (matchingItems.length === 0) {
        // Try dropping the trailing digit (check digit variation)
        const withoutTrailing = normalizedCode.substring(0, normalizedCode.length - 1);
        console.log('Book Mode: Trying without trailing digit:', withoutTrailing);
        matchingItems = searchWithCode(withoutTrailing);
      }
    }

    console.log('Book Mode: Final result -', matchingItems.length, 'matching items');

    // Debug: Log first item's identifiers to understand structure
    if (matchingItems.length === 0 && shipmentItems.length > 0) {
      console.log('Book Mode DEBUG: First item identifiers:', shipmentItems[0]?.item?.identifiers);
      console.log('Book Mode DEBUG: First item SKU:', shipmentItems[0]?.item?.merchant_sku);
      console.log('Book Mode DEBUG: First item ASIN:', shipmentItems[0]?.item?.asin);
    }

    return matchingItems;
  }, [shipmentItems]);

  // Book Mode: Process a single item (print label + add to box)
  const processBookModeItem = useCallback(async (shipmentItem, scannedCode, boxNumber) => {
    const item = shipmentItem.item;
    console.log('Book Mode: processBookModeItem called with boxNumber:', boxNumber);

    // Find which identifier matched
    const normalizedCode = scannedCode.trim().replace(/-/g, '');
    const identifiers = item.identifiers || [];
    let matchedIdentifier = null;
    let matchType = null;

    // Check exact match first
    for (const id of identifiers) {
      const value = id.identifier?.replace(/-/g, '');
      if (value === normalizedCode || value?.toUpperCase() === normalizedCode.toUpperCase()) {
        matchedIdentifier = id.identifier;
        matchType = id.identifier_type;
        break;
      }
    }

    // Check partial match (leading/trailing digit dropped)
    if (!matchedIdentifier && normalizedCode.length >= 10) {
      const withoutLeading = normalizedCode.substring(1);
      const withoutTrailing = normalizedCode.substring(0, normalizedCode.length - 1);

      for (const id of identifiers) {
        const value = id.identifier?.replace(/-/g, '');
        if (value === withoutLeading || value === withoutTrailing) {
          matchedIdentifier = id.identifier;
          matchType = id.identifier_type + ' (partial)';
          break;
        }
      }
    }

    // Fallback to SKU/ASIN match
    if (!matchedIdentifier) {
      if (item.merchant_sku?.toUpperCase() === normalizedCode.toUpperCase()) {
        matchedIdentifier = item.merchant_sku;
        matchType = 'SKU';
      } else if (item.asin?.toUpperCase() === normalizedCode.toUpperCase()) {
        matchedIdentifier = item.asin;
        matchType = 'ASIN';
      }
    }

    // Show processing state
    setBookModeResult({
      success: null, // null = processing
      message: 'Processing...',
      item: {
        title: item.title,
        merchant_sku: item.merchant_sku,
        asin: item.asin,
        fnsku: item.fnsku,
        images: item.images,
      },
    });
    setShowBookModeOverlay(true);

    try {
      // For US shipments, fetch country of origin first
      let countryOfOrigin = '';
      if (clientInfo?.country === 'USA' && item.asin) {
        try {
          const SERVER_API_URL = import.meta.env.VITE_SERVER_API_URL || 'http://localhost:5000';
          const importInfoResponse = await fetch(`${SERVER_API_URL}/api/usa-import-info/${item.asin}`);
          if (importInfoResponse.ok) {
            const importData = await importInfoResponse.json();
            countryOfOrigin = importData?.country_of_origin || '';
          }
        } catch (err) {
          console.warn('Book Mode: Failed to fetch country of origin:', err);
        }
      }

      // Step 1: Generate FNSKU label (print or save as PDF based on bookModeAutoPrint)
      const printerToUse = bookModeAutoPrint ? bookModePrinter : null;
      await new Promise((resolve, reject) => {
        generatePDF(
          shipmentItem, // item object
          1, // quantity = 1
          shipmentItem.expiry_date ? new Date(shipmentItem.expiry_date) : null, // expiryDate
          null, // option (no special label type)
          0, // separatorInterval
          0, // sequenceSize
          printerToUse, // printer ID (null = save as PDF)
          (result) => {
            if (result.success) {
              resolve(result);
            } else {
              reject(new Error(result.message || 'Label generation failed'));
            }
          },
          clientInfo, // clientInfo for country of origin check
          countryOfOrigin // countryOfOrigin from import info
        );
      });

      // Step 2: Add 1 unit to the selected box
      const currentBox = boxes.find(b => String(b.boxNumber) === String(boxNumber));

      const newItem = {
        shipmentId,
        boxNumber: boxNumber,
        sku: item.merchant_sku,
        asin: item.asin,
        fnsku: item.fnsku,
        qty: 1,
        length: currentBox?.length,
        width: currentBox?.width,
        height: currentBox?.height,
        title: item.title,
        weight: contextAsinData?.find(a => a.ASIN === item.asin)?.WEIGHT || item.weight || '',
        pallet: currentBox?.PALLET,
        employee: selectedNickname,
        images: item.images,
      };

      const response = await fetch(`${API_URL}/api/add_item_to_box_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to add item to box');
      }

      // Refresh boxes
      await refreshBoxes();

      // Play success sound
      playBookModeSuccess();

      // Show success
      setBookModeResult({
        success: true,
        message: bookModeAutoPrint ? 'Label printed & item added!' : 'Label saved & item added!',
        item: {
          title: item.title,
          merchant_sku: item.merchant_sku,
          asin: item.asin,
          fnsku: item.fnsku,
          images: item.images,
        },
        scannedCode: scannedCode,
        matchedIdentifier: matchedIdentifier,
        matchType: matchType,
        labelSaved: !bookModeAutoPrint, // Track if label was saved vs printed
      });

      // Auto-dismiss after 2 seconds
      setTimeout(() => {
        setShowBookModeOverlay(false);
        setBookModeResult(null);
      }, 2000);

    } catch (error) {
      console.error('Book Mode error:', error);

      // Play error sound
      playBookModeError();

      setBookModeResult({
        success: false,
        message: error.message || 'Operation failed',
        item: {
          title: item.title,
          merchant_sku: item.merchant_sku,
        },
      });

      // Keep error visible longer
      setTimeout(() => {
        setShowBookModeOverlay(false);
        setBookModeResult(null);
      }, 5000);
    }
  }, [bookModePrinter, bookModeAutoPrint, clientInfo, boxes, shipmentId, selectedNickname, refreshBoxes, contextAsinData, playBookModeSuccess, playBookModeError]);

  // Book Mode: Handle barcode scan
  const handleBookModeScan = useCallback(async (scannedCode) => {
    // Use refs to get current values (avoids stale closure issues with barcode scanner hook)
    const currentOpenBoxNumber = openBoxNumberRef.current;
    const currentSelectedBox = selectedBoxRef.current;

    console.log('Book Mode scan detected:', scannedCode);
    console.log('Book Mode: openBoxNumber (ref) =', currentOpenBoxNumber);
    console.log('Book Mode: bookModePrinter =', bookModePrinter);
    console.log('Book Mode: selectedBox (ref) =', currentSelectedBox);

    // Validation checks - check both openBoxNumber and selectedBox
    const activeBoxNumber = currentOpenBoxNumber || currentSelectedBox?.boxNumber;

    if (!activeBoxNumber) {
      console.log('Book Mode: No box selected - openBoxNumber:', currentOpenBoxNumber, 'selectedBox:', currentSelectedBox);
      playBookModeError();
      setBookModeResult({
        success: false,
        message: 'No box selected. Please click on a box to open it first.',
      });
      setShowBookModeOverlay(true);
      setTimeout(() => {
        setShowBookModeOverlay(false);
        setBookModeResult(null);
      }, 3000);
      return;
    }

    // Only require printer when auto-print is enabled
    if (bookModeAutoPrint && !bookModePrinter) {
      playBookModeError();
      setBookModeResult({
        success: false,
        message: 'No printer configured. Please select a printer or disable auto-print.',
      });
      setShowBookModeOverlay(true);
      setTimeout(() => {
        setShowBookModeOverlay(false);
        setBookModeResult(null);
      }, 3000);
      return;
    }

    // Find matching items
    const matchingItems = findItemByBarcode(scannedCode);

    if (matchingItems.length === 0) {
      playBookModeError();
      setBookModeResult({
        success: false,
        message: `No item found for barcode: ${scannedCode}`,
      });
      setShowBookModeOverlay(true);
      setTimeout(() => {
        setShowBookModeOverlay(false);
        setBookModeResult(null);
      }, 3000);
      return;
    }

    // Handle multiple matches - show selection popup
    if (matchingItems.length > 1) {
      setBookModeResult({
        success: false,
        multipleMatches: true,
        matches: matchingItems,
        scannedCode,
      });
      setShowBookModeOverlay(true);
      return;
    }

    // Single match - process it with the active box number
    await processBookModeItem(matchingItems[0], scannedCode, activeBoxNumber);
  }, [bookModePrinter, bookModeAutoPrint, findItemByBarcode, processBookModeItem, playBookModeError]);

  // Initialize Book Mode barcode scanner
  // Note: Enabled when bookModeEnabled is true, but will show error if no box is selected when scan occurs
  useBarcodeScanner({
    onScan: handleBookModeScan,
    enabled: bookModeEnabled,
    minLength: 8, // UPC-E is 8 characters, standard UPC is 12, EAN is 13, ISBN is 10 or 13
    maxDelay: 50,
  });

  if (!selectedNickname) {
    return <NicknameModal nicknames={nicknames} onSelect={handleNicknameSelect} />;
  }

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!shipmentInfo || !shipmentItems) {
    return <div className="p-4">No data available</div>;
  }

  const statusCounts = {
    NotStarted: 0,
    InProgress: 0,
    Completed: 0,
    OverLimit: 0
  };

  const skuCount = shipmentItems.length;
  const itemCount = shipmentItems.reduce((total, item) => total + item.quantity, 0);

  // Helper function to find which fields match the search term
  const getMatchingFields = (item, searchTerm) => {
    const matches = {
      fields: [],
      upcs: [],
      eans: []
    };
    
    if (!searchTerm) return matches;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    // Check basic fields
    if (item.item.merchant_sku && item.item.merchant_sku.toLowerCase().includes(lowerSearchTerm)) {
      matches.fields.push('sku');
    }
    if (item.item.title && item.item.title.toLowerCase().includes(lowerSearchTerm)) {
      matches.fields.push('title');
    }
    if (item.item.asin && item.item.asin.toLowerCase().includes(lowerSearchTerm)) {
      matches.fields.push('asin');
    }
    if (item.item.fnsku && item.item.fnsku.toLowerCase().includes(lowerSearchTerm)) {
      matches.fields.push('fnsku');
    }
    
    // Check UPCs and EANs
    if (item.item.identifiers && Array.isArray(item.item.identifiers)) {
      item.item.identifiers.forEach(identifier => {
        if (identifier.identifier_type === 'UPC' && identifier.identifier.toLowerCase().includes(lowerSearchTerm)) {
          matches.upcs.push(identifier.identifier);
        }
        if (identifier.identifier_type === 'EAN' && identifier.identifier.toLowerCase().includes(lowerSearchTerm)) {
          matches.eans.push(identifier.identifier);
        }
      });
    }
    
    return matches;
  };

  // Use selectedPlan if available (smart loading), otherwise fall back to most recent plan
  const planToUseForFilter = selectedPlan || (plans?.length > 0 
    ? plans.reduce((latest, current) => 
        new Date(current.created_at) > new Date(latest.created_at) ? current : latest
      )
    : null);

  // Get packing groups from the plan to use for filter display
  const availablePackingGroups = planToUseForFilter?.packing_options
    ? (() => {
        const confirmedOptions = planToUseForFilter.packing_options.filter(po => po.confirmed_at);
        
        if (confirmedOptions.length > 0) {
          // Use confirmed options first
          const latestOption = confirmedOptions.reduce((latest, current) => 
            new Date(current.confirmed_at) > new Date(latest.confirmed_at) ? current : latest
          );
          return latestOption.packing_groups || [];
        } else if (planToUseForFilter.packing_options.length > 0) {
          // If no confirmed options, use the most recent unconfirmed option
          const latestOption = planToUseForFilter.packing_options.reduce((latest, current) => 
            new Date(current.created_at || current.updated_at || 0) > new Date(latest.created_at || latest.updated_at || 0) ? current : latest
          );
          return latestOption.packing_groups || [];
        }
        return [];
      })()
    : [];

  // Debug for packing group filter - only show when there should be multiple groups
  if (plans?.length > 0) {
    console.log('PACKING GROUP DEBUG:', {
      totalPlans: plans.length,
      planToUse: planToUseForFilter?.id,
      availablePackingGroups: availablePackingGroups.length,
      packingOptions: planToUseForFilter?.packing_options?.length,
      confirmedOptions: planToUseForFilter?.packing_options?.filter(po => po.confirmed_at).length,
      packingOptionsDetails: planToUseForFilter?.packing_options?.map(po => ({
        id: po.id,
        hasConfirmedAt: !!po.confirmed_at,
        packingGroupsCount: po.packing_groups?.length || 0
      }))
    });
  }

  // Create packing group filter options
  const packingGroupFilterOptions = availablePackingGroups.map((group, index) => {
    // Calculate SKU count and total units for this packing group
    const planItemsMap = new Map(
      planToUseForFilter.items.map(item => [item.id, item])
    );
    
    const groupItems = group.items
      .map(item => planItemsMap.get(item.planItemId))
      .filter(Boolean);
    
    const skuCount = groupItems.length;
    const totalUnits = groupItems.reduce((sum, item) => sum + item.quantity, 0);
    
    return {
      value: group,
      label: `Packing Group ${index + 1} • ${skuCount} SKUs • ${totalUnits} Units`
    };
  });

  // Corrected filtering logic - only show items from selected plan
  let itemsToFilter = shipmentItems;

  
  if (planToUseForFilter) {
    itemsToFilter = shipmentItems
      .filter(item => planToUseForFilter.items.some(planItem => planItem.msku === item.item.merchant_sku))
      .map(item => {
        const planItem = planToUseForFilter.items.find(planItem => planItem.msku === item.item.merchant_sku);
        return { ...item, quantity: planItem.quantity };
      });
  }

  let searchedItems = itemsToFilter.filter(item => {
    if (!itemSearchTerm || itemSearchTerm.trim() === '') {
      return true;
    }
    const matches = getMatchingFields(item, itemSearchTerm);
    return matches.fields.length > 0 || matches.upcs.length > 0 || matches.eans.length > 0;
  }).map(item => ({
    ...item,
    _searchMatches: getMatchingFields(item, itemSearchTerm),
    _searchTerm: itemSearchTerm
  }));

  let preStatusFilteredItems = applyAllFilters(searchedItems);
  
  // Store the count before packing group filter for UI display
  const itemsBeforePackingGroupFilter = preStatusFilteredItems.length;
  
  // Apply packing group filter if one is selected
  if (selectedPackingGroupFilter && planToUseForFilter) {
    const planItemsMap = new Map(
      planToUseForFilter.items.map(item => [item.id, item.msku])
    );
    
    const packingGroupMskus = new Set(
      selectedPackingGroupFilter.items
        .map(item => planItemsMap.get(item.planItemId))
        .filter(Boolean)
    );
    
    preStatusFilteredItems = preStatusFilteredItems.filter(item => 
      packingGroupMskus.has(item.item.merchant_sku)
    );
  }
  
  preStatusFilteredItems = filterItemsByPackingGroup(preStatusFilteredItems, selectedPackingGroup, showItemsForAllPlacements);

  // Apply quantity sorting
  if (quantitySortOrder) {
    preStatusFilteredItems = [...preStatusFilteredItems].sort((a, b) => {
      const quantityA = a.quantity;
      const quantityB = b.quantity;
      
      if (quantitySortOrder === 'asc') {
        return quantityA - quantityB;
      } else {
        return quantityB - quantityA;
      }
    });
  }

  preStatusFilteredItems.forEach(item => {
    const status = getItemStatus(item);
    if (statusCounts[status] !== undefined) {
      statusCounts[status]++;
    }
  });

  const filteredShipmentItems = filterItemsByStatus(preStatusFilteredItems, statusFilter);
  const totalSKUsAfterFilter = preStatusFilteredItems.length;

  // CHANGED: Build planOptions with isActive
  const groupedPlans = plans.reduce((acc, plan) => {
    const date = moment(plan.created_at).format('MMM D, h:mm:ss A');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(plan);
    return acc;
  }, {});

  const planOptions = Object.keys(groupedPlans)
    .sort((a, b) => new Date(b) - new Date(a))
    .map(date => ({
      label: date,
      options: groupedPlans[date]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(plan => {
          const planId = plan.inbound_plan_id;
          const truncatedId = planId.length > 10 
            ? `${planId.slice(0, 5)}...${planId.slice(-5)}`
            : planId;
          return {
            value: plan.id,
            label: `${truncatedId} | ${plan.items.length} SKUs - ${plan.items.reduce((sum, item) => sum + item.quantity, 0)} Units${
              plan.is_canceled ? ' (Cancelled)' : plan.is_accepted ? ' (Accepted)' : ''
            }`,
            isActive: plan.status === 'ACTIVE', // NEW
          };
        })
    }));

  const packingGroupOptions = packingGroups?.map(group => {
    const skuSet = new Set();
    let totalUnits = 0;
    group.items.forEach(item => {
      skuSet.add(item.msku);
      totalUnits += item.quantity;
    });
    return {
      value: group,
      label: `(...${group.id.slice(-3)}) ${skuSet.size} SKUs - ${totalUnits} Units`
    };
  }) || [];

  const totalPages = Math.ceil(filteredShipmentItems.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredShipmentItems.slice(indexOfFirstItem, indexOfLastItem);

  const handleShowReassignModal = (mainOutboundId) => {
    setMainOutboundId(mainOutboundId);
    setShowReassignPlanModal(true);
  };

  const handleTitleMouseEnter = () => {
    setTooltipContent(shipmentInfo.name);
  };

  const getDaysAgo = (date) => {
    const days = dayjs().diff(dayjs(date), 'day');
    return days === 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;
  };

  // First, add these custom styles for the Select components
  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: 'white',
      borderColor: '#e2e2e2',
    }),
    option: (base, state) => ({
      ...base,
      color: '#1a365d',
      backgroundColor: state.isFocused ? '#f3f4f6' : 'white',
      '&:hover': {
        backgroundColor: '#f3f4f6',
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1a365d',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'white',
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: 'white',
    }),
    input: (base) => ({
      ...base,
      color: '#1a365d',
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#6b7280',
      '&:hover': {
        color: '#1a365d',
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#6b7280',
      '&:hover': {
        color: '#1a365d',
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#f3f4f6',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#1a365d',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#6b7280',
      '&:hover': {
        backgroundColor: '#e5e7eb',
        color: '#1a365d',
      },
    }),
  };

  // DutiesInfo component - Handles duties display and checks
  const DutiesInfo = ({ htsOption, countryOfOrigin }) => {
    const [duties, setDuties] = useState(null);

    // Effect to check for duties whenever country changes to/from China
    useEffect(() => {
      const checkDuties = async () => {
        if (countryOfOrigin?.toLowerCase() === 'china') {
          try {
            const response = await fetch(`${API_URL}/api/check-section-301`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ htsCode: htsOption.hts_code })
            });
            const dutiesData = await response.json();
            setDuties(dutiesData);
          } catch (error) {
            console.error('Error checking duties:', error);
          }
        } else {
          setDuties(null);
        }
      };

      if (htsOption?.hts_code) {
        checkDuties();
      }
    }, [countryOfOrigin, htsOption?.hts_code]);

    // Only render duties section if country is China and we have duties data
    if (countryOfOrigin?.toLowerCase() === 'china' && duties) {
      return (
        <div className="duties-info mt-3 p-3 bg-gray-50 rounded">
          <h4 className="text-red-600 font-semibold mb-2">Duties Applicable (estimated):</h4>
          {duties.section301 && (
            <div className="section-301 mb-1">
              <span className="duty-label font-medium">Section 301 Duty: </span>
              <span className="duty-value">{duties.section301}</span>
            </div>
          )}
          {duties.feb2025Tariff && (
            <div className="additional-tariff">
              <span className="duty-label font-medium">Additional Tariff: </span>
              <span className="duty-value">{duties.feb2025Tariff}</span>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // HTSDisplay component - Always shows classification info
  const HTSDisplay = ({ htsOption, countryOfOrigin }) => {
    if (!htsOption) return null;

    return (
      <div className="hts-info bg-white p-4 rounded-lg shadow">
        <div className="mb-2">
          <strong className="text-gray-700">HTS Subheading:</strong>
          <span className="ml-2">{htsOption.hts_code}</span>
        </div>
        <div className="mb-3">
          <strong className="text-gray-700">Classification:</strong>
          <span className="ml-2">{htsOption.classification_name}</span>
        </div>
        {htsOption.confidence_score && (
          <div className="mb-3 text-sm text-gray-600">
            <strong>Confidence Score:</strong>
            <span className="ml-2">{Math.round(htsOption.confidence_score)}%</span>
          </div>
        )}
        <DutiesInfo 
          htsOption={htsOption}
          countryOfOrigin={countryOfOrigin}
        />
      </div>
    );
  };

  const handleOptimize = async (selectedItems, updateProgress) => {
    try {
      // Step 1: Create new outbound with remaining quantities
      updateProgress('Creating new outbound for remaining quantities...', 20);
      
      const newOutboundResponse = await fetch(`${API_URL}/api/create_remainder_outbound`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `Optimized Remainder [${shipmentInfo.name}]`,
          team_id: shipmentInfo.team_id,
          original_outbound_id: shipmentInfo.id,
          items: selectedItems.map(item => ({
            item_id: item.item.id,
            quantity: item.remainingQuantity,
            expiry_date: item.expiry_date
          }))
        })
      });

      if (!newOutboundResponse.ok) {
        throw new Error(`Failed to create remainder outbound: ${newOutboundResponse.statusText}`);
      }

      const newOutboundData = await newOutboundResponse.json();
      const newOutboundId = newOutboundData.shipment_id;

      // Step 2: Update original outbound to remove remainder quantities
      updateProgress('Updating original outbound quantities...', 60);

      const updateResponse = await fetch(`${API_URL}/api/update_remainder_quantities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          original_outbound_id: shipmentInfo.id,
          items: selectedItems.map(item => ({
            item_id: item.item.id,
            remainder_quantity: item.remainingQuantity
          }))
        })
      });

      if (!updateResponse.ok) {
        throw new Error(`Failed to update original outbound quantities: ${updateResponse.statusText}`);
      }

      // Step 3: Refresh the current shipment data
      updateProgress('Refreshing shipment data...', 80);
      await reloadShipmentData(); // This should trigger a refresh of the current page data

      updateProgress('Complete!', 100);

      // Calculate summary
      const summary = {
        totalSKUs: selectedItems.length,
        totalUnits: selectedItems.reduce((sum, item) => sum + item.remainingQuantity, 0)
      };

      // Open the new outbound in a new tab
      window.open(`https://app.yyzprep.ca/staff/shipments/outbound/${newOutboundId}`, '_blank');

      return {
        success: true,
        message: "Remainder outbound created and original quantities updated successfully.",
        summary,
        newShipmentId: newOutboundId,
        selectedItems
      };

    } catch (error) {
      console.error('Optimization failed:', error);
      throw error;
    }
  };

  // Update monitorOperation to use our server endpoint
  const monitorOperation = async (operationId) => {
    let status = 'IN_PROGRESS';
    while (status === 'IN_PROGRESS') {
      const response = await fetch(`${API_URL}/api/operation_status/${operationId}`);
      const data = await response.json();
      status = data.data.status;
      
      if (status === 'IN_PROGRESS') {
        await new Promise(resolve => setTimeout(resolve, 2000));
      } else if (status !== 'SUCCESS') {
        const nonWarningProblems = data.data.problems?.filter(
          p => p.severity !== 'WARNING'
        );
        if (nonWarningProblems?.length > 0) {
          throw new Error(`Operation failed: ${nonWarningProblems[0].message}`);
        }
      }
    }
  };

  // Update the handleGeneratePlan function
  const handleGeneratePlan = async () => {
    try {
      setIsGeneratingPlan(true);
      setErrorMessage(null);
      
      // Step 1: Get channel ID
      console.log('Step 1: Fetching channel ID for team:', shipmentInfo?.team_id);
      if (!shipmentInfo?.team_id) {
        throw new Error('No team_id available in shipmentInfo');
      }

      const channelResponse = await fetch(`${API_URL}/api/channels/${shipmentInfo.team_id}`);
      if (!channelResponse.ok) {
        const errorText = await channelResponse.text();
        throw new Error(`Failed to fetch channels: ${errorText}`);
      }
      const channelData = await channelResponse.json();
      
      if (!channelData?.channels?.length) {
        throw new Error('No channels found for this team');
      }
      const channelId = channelData.channels[0].id;
      console.log('Channel ID fetched:', channelId);

      // Step 2: Create plan items array from shipment items
      console.log('Step 2: Creating plan items from shipment items');
      if (!shipmentItems?.length) {
        throw new Error('No shipment items available');
      }

      const planItems = shipmentItems.map(item => {
        if (!item?.item?.merchant_sku) {
          console.warn('Missing merchant_sku for item:', item);
        }
        return {
          expiration: item.expiry_date || null,
          label_owner: "SELLER",
          msku: item.item.merchant_sku,
          prep_owner: "SELLER",
          quantity: item.quantity,
          manufacturing_lot_code: null
        };
      });
      console.log('Plan items created:', planItems);

      // Step 3: Create the plan
      console.log('Step 3: Creating plan with data:', {
        outbound_id: shipmentId,
        channel_id: channelId,
        items_count: planItems.length
      });

      console.log('Making create plan request with:', {
        url: `${API_URL}/api/create_plan`,
        body: {
          outbound_id: shipmentId,
          channel_id: channelId,
          items: planItems
        }
      });

      const planResponse = await fetch(`${API_URL}/api/create_plan`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          outbound_id: shipmentId,
          channel_id: channelId,
          items: planItems
        })
      });

      console.log('Create plan response:', {
        ok: planResponse.ok,
        status: planResponse.status,
        statusText: planResponse.statusText
      });

      if (!planResponse.ok) {
        const errorText = await planResponse.text();
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) {
            try {
              // Try to parse the nested error JSON
              const parsedError = JSON.parse(errorData.error);
              if (parsedError.errors && parsedError.errors.length > 0) {
                throw new Error(parsedError.errors.join('\n'));
              }
            } catch (e) {
              // If parsing fails, just use the original error message
              throw new Error(errorData.error);
            }
          }
          throw new Error(errorText);
        } catch (e) {
          throw new Error(`Failed to create plan: ${e.message}`);
        }
      }

      const planData = await planResponse.json();
      console.log('Plan created:', planData);

      if (!planData?.data?.id) {
        throw new Error('Plan created but no plan ID received');
      }
      const planId = planData.data.id;
      const operationId = planData.data.create_plan_operation_id;

      // Step 4: Monitor plan creation
      console.log('Step 4: Monitoring plan creation operation:', operationId);
      await monitorOperation(operationId);
      console.log('Plan creation completed');

      // Step 5: Trigger packing options
      console.log('Step 5: Triggering packing options for plan:', planId);
      const triggerPackingOptionsResponse = await fetch(
        `https://app.yyzprep.ca/api/fba-transport/v2024/plans/${planId}/packing-options?plan_id=${planId}&api_token=${API_TOKEN}`
      );

      if (!triggerPackingOptionsResponse.ok) {
        const errorText = await triggerPackingOptionsResponse.text();
        throw new Error(`Failed to trigger packing options: ${errorText}`);
      }
      console.log('Packing options triggered');

      // Step 6: Wait for packing options (retry up to 3 times)
      console.log('Step 6: Waiting for packing options to be generated');
      let attempts = 0;
      let packingOptionsGenerated = false;
      while (attempts < 3 && !packingOptionsGenerated) {
        console.log(`Attempt ${attempts + 1}/3 to check packing options`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const checkResponse = await fetch(
          `https://app.yyzprep.ca/api/fba-transport/v2024/plans/${planId}?api_token=${API_TOKEN}`
        );
        
        if (!checkResponse.ok) {
          const errorText = await checkResponse.text();
          console.warn(`Failed to check plan status on attempt ${attempts + 1}:`, errorText);
        } else {
          const updatedPlanData = await checkResponse.json();
          if (updatedPlanData.data.packing_options?.length > 0) {
            console.log('Packing options found:', updatedPlanData.data.packing_options.length);
            packingOptionsGenerated = true;
            break;
          }
        }
        attempts++;
      }

      if (!packingOptionsGenerated) {
        throw new Error('Failed to generate packing options after 3 attempts');
      }

      // Step 7: Reload shipment data to get updated plans
      console.log('Step 7: Reloading shipment data');
      await reloadShipmentData();
      
      // Step 8: Show success message
      console.log('Step 8: Plan generation completed successfully');
      showSnackbar('Plan generated successfully!', 'success');
      setShowGeneratePlanModal(false);

    } catch (error) {
      console.error('Error generating plan:', error);
      setErrorMessage(error.message);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const handleDebugRefresh = async () => {
    try {
      console.log('Manual data refresh initiated...');
      await Promise.all([
        refreshBoxes(),
        reloadShipmentData()
      ]);
      console.log('Manual data refresh completed successfully');
      showSnackbar('Data refreshed successfully - try submitting again', 'success');
    } catch (error) {
      console.error('Debug refresh failed:', error);
      showSnackbar('Refresh failed', 'error');
    }
  };

  // Bulk selection handler functions
  const handleItemSelect = (itemId, isSelected) => {
    setSelectedItems(prev => {
      const newSelection = new Set(prev);
      if (isSelected) {
        newSelection.add(itemId);
      } else {
        newSelection.delete(itemId);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (isSelectAll) => {
    if (isSelectAll) {
      const allItemIds = new Set(filteredShipmentItems.map(item => item.id));
      setSelectedItems(allItemIds);
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleBulkPrint = () => {
    if (selectedItems.size === 0) {
      showSnackbar('Please select at least one item to print', 'warning');
      return;
    }
    setShowBulkPrintModal(true);
  };

  // Add quantity sorting function
  const handleQuantitySort = () => {
    if (quantitySortOrder === null || quantitySortOrder === 'desc') {
      setQuantitySortOrder('asc');
    } else {
      setQuantitySortOrder('desc');
    }
  };

  // Add function to get all box validation errors (similar to BoxesPanel)
  const getAllBoxValidationErrors = () => {
    // Use the same effective ASIN data logic as BoxesPanel
    const effectiveAsinData = asinData && asinData.length > 0 ? asinData : (contextAsinData || []);
    
          if (!boxes || boxes.length === 0) {
        return [];
      }

    // Helper function to check for valid weight
    const isValidWeight = (weight) => {
      const num = parseFloat(weight);
      return !isNaN(num) && num > 0;
    };

    // Helper to calculate expected weight from items
    const calculateExpectedWeight = (box) => {
      if (!box.items || !effectiveAsinData) {
        return null;
      }
      
      let hasAllWeights = true;
      const itemsTotalWeight = box.items.reduce((acc, item) => {
        const asinRow = effectiveAsinData.find(a => a.ASIN === item.ASIN);
        
        let unitWeight = null;
        
        // Prioritize weight from asinData if it's valid
        if (asinRow && isValidWeight(asinRow.WEIGHT)) {
          unitWeight = parseFloat(asinRow.WEIGHT);
        } 
        // Fallback to the item's own WEIGHT property if it's valid
        else if (isValidWeight(item.WEIGHT)) {
          unitWeight = parseFloat(item.WEIGHT);
        }

        if (unitWeight !== null) {
          const quantity = parseInt(item.QTY, 10) || 0;
          const itemTotal = unitWeight * quantity;
          return acc + itemTotal;
        } else {
          hasAllWeights = false;
          return acc;
        }
      }, 0);

      if (!hasAllWeights) return null;
      
      const boxWeight = 1; // 1kg for the box itself
      return itemsTotalWeight + boxWeight;
    };

    const boxErrors = [];

    boxes.forEach(box => {
      const errors = [];
      
      // Check missing dimensions
      if (!box.length || box.length <= 0) errors.push('Missing length');
      if (!box.width || box.width <= 0) errors.push('Missing width');
      if (!box.height || box.height <= 0) errors.push('Missing height');
      
      // Check missing or invalid weight
      if (!box.weight || box.weight <= 0) {
        errors.push('Missing weight');
      } else if (box.weight < 0.3) {
        errors.push('Weight too low (<0.3kg)');
      } else if (box.weight > 21) {
        errors.push('CRITICAL: Weight exceeds 21kg limit!');
      }

      // Check for weight mismatch
      const expectedWeight = calculateExpectedWeight(box);
      const currentWeight = parseFloat(box.weight) || 0;
      
      if (expectedWeight && currentWeight > 0) {
        const weightDifference = Math.abs(currentWeight - expectedWeight);
        if (weightDifference > 0.1) { // 0.1kg tolerance
          errors.push(`Weight mismatch: Manual ${currentWeight}kg vs Calculated ${expectedWeight.toFixed(2)}kg`);
        }
      }
      
      // Calculate total box value from items
      const calculateBoxValue = () => {
        if (!box.items) return 0;
        
        let total = 0;
        
        box.items.forEach(item => {
          const asinDataItem = effectiveAsinData?.find(data => data.ASIN === item.ASIN);
          const value = asinDataItem?.VALUE;
          const qty = parseInt(item.QTY || 0);
          
          if (value && !isNaN(parseFloat(value)) && qty > 0) {
            const itemTotal = parseFloat(value) * qty;
            total += itemTotal;
          }
        });
        
        return total;
      };

      const totalValue = calculateBoxValue();
      
      // Check if box value exceeds $800 (only for US shipments)
      const isUSShipment = clientInfo?.country && (
        clientInfo.country.toUpperCase() === 'US' || 
        clientInfo.country.toUpperCase() === 'USA' || 
        clientInfo.country.toUpperCase() === 'UNITED STATES'
      );
      
      if (isUSShipment && totalValue > 800) {
        errors.push(`Value exceeds $800 (${totalValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})`);
      }

      if (errors.length > 0) {
        boxErrors.push({
          boxNumber: box.boxNumber,
          visualId: box.visualId || `#${box.boxNumber}`,
          errors: errors
        });
      }
    });

    return boxErrors;
  };

  return (
          <div className="min-h-screen bg-gray-50">
      
      {/* Compact Single Line Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-4 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            
            {/* Left: Title & Status */}
            <div className="flex items-center space-x-2 min-w-0 flex-shrink-0">
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-sm font-semibold text-slate-900 truncate cursor-pointer max-w-xs">
                    {shipmentInfo.name.length > 50
                      ? `${shipmentInfo.name.substring(0, 50)}...`
                      : shipmentInfo.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-white text-black">
                  <p>{shipmentInfo.name}</p>
                </TooltipContent>
              </Tooltip>
              <Badge 
                variant={shipmentInfo.status === 'ACTIVE' ? 'success' : 'secondary'}
                className="text-xs px-2 py-0.5 flex-shrink-0"
              >
                {shipmentInfo.status}
              </Badge>
            </div>

            {/* Center: Compact Info */}
            <div className="flex items-center space-x-4 text-xs text-slate-600 flex-1 justify-center">
              <a
                href={`https://app.yyzprep.ca/staff/shipments/outbound/${shipmentInfo.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                #{shipmentInfo.id}
              </a>
              <span className="text-slate-400">•</span>
              <span className="font-medium truncate max-w-24">{clientInfo?.name?.split('(')[0]?.trim() || 'N/A'}</span>
              <span className="text-slate-400">•</span>
              <span className="font-medium">{selectedNickname}</span>
              <span className="text-slate-400">•</span>
              <span>{getDaysAgo(shipmentInfo.created_at)}</span>
            </div>

            {/* Right: Compact Controls */}
            <div className="flex items-center flex-wrap justify-end gap-3 flex-shrink-0">
              
              {/* Role Selector */}
              <RoleSelector />
              
              {/* Auto Print Toggle */}
              <div className="flex items-center space-x-1.5">
                <Switch
                  checked={autoPrintEnabled}
                  onCheckedChange={handleAutoPrintToggle}
                  className="data-[state=checked]:bg-blue-600 scale-75"
                />
                <span className="text-xs text-slate-700 whitespace-nowrap">Auto Print</span>
              </div>

              {/* Book Mode Active Indicator */}
              {bookModeEnabled && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                  <BookOpen className="w-3 h-3" />
                  <span>Book Mode</span>
                </div>
              )}

              {/* Compact Progress */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-slate-500">Units:</span>
                  <span className="text-xs font-semibold text-slate-900">{unitsPacked}/{totalUnits}</span>
                  <span className="text-xs text-slate-500">{Math.round(unitProgress)}%</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-slate-500">SKUs:</span>
                  <span className="text-xs font-semibold text-slate-900">{skusCompleted}/{totalSKUs}</span>
                  <span className="text-xs text-slate-500">{Math.round(skuProgress)}%</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => {
                    setIsValidationConfirmed(false);
                    setCurrentSubmissionPlan(null);
                    handleSubmitBoxContents();
                  }}
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-1.5 text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <SaveIcon className="w-3 h-3 mr-1.5" />
                      Submit
                    </>
                  )}
                </Button>
                <ActionsMenu
                  onSplit={() => setShowSplitShipmentModal(true)}
                  onBulkFees={() => setShowBulkFeesModal(true)}
                  onDownloadUPS={downloadUPSLabels}
                  onExport={exportToExcel}
                  onImport={() => fileInputRef.current?.click()}
                  onImportFromOutbounds={() => setShowOutboundImportModal(true)}
                  onContact={() => setShowSendEmailModal(true)}
                  onPickingList={generatePickingList}
                  onReset={() => setShowResetDialog(true)}
                  onOptimize={() => setShowOptimizeModal(true)}
                  onUSAHelper={() => window.open(`http://boxcontents.yyzprep.ca/usa/${shipmentId}`, '_blank')}
                  onDebugRefresh={handleDebugRefresh}
                  onPlanPlacementFees={() => setShowPlanPlacementFeesModal(true)}
                  onVisionScanner={() => setShowVisionDetector(true)}
                  isUSAShipment={clientInfo?.country === 'USA'}
                  bookModeEnabled={bookModeEnabled}
                  onBookModeToggle={setBookModeEnabled}
                  bookModePrinter={bookModePrinter}
                  onBookModePrinterChange={setBookModePrinter}
                  bookModeAutoPrint={bookModeAutoPrint}
                  onBookModeAutoPrintToggle={setBookModeAutoPrint}
                  availablePrinters={availablePrinters}
                  openBoxNumber={openBoxNumber}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Notes Section */}
      <div className="mx-6 mb-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-start justify-between space-x-6">
            {/* Client Notes */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <InfoIcon className="w-3 h-3 text-slate-400" />
                  <span className="text-xs font-medium text-slate-600">Client Notes</span>
                </div>
                {shipmentInfo.notes && shipmentInfo.notes.trim() && shipmentInfo.notes.split('\n').length > 7 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsClientNotesExpanded(!isClientNotesExpanded)}
                    className="h-6 px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
                  >
                    {isClientNotesExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                )}
              </div>
              {shipmentInfo.notes && shipmentInfo.notes.trim() ? (
                <div 
                  className="text-xs text-red-700 bg-yellow-200 rounded-md p-2 border border-yellow-300 transition-all duration-200" 
                  style={{ 
                    whiteSpace: 'pre-line',
                    maxHeight: isClientNotesExpanded ? 'none' : '126px', // ~7 lines for text-xs
                    overflowY: isClientNotesExpanded ? 'visible' : 'auto'
                  }}
                >
                  {shipmentInfo.notes}
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic">No client notes</div>
              )}
            </div>

            {/* Warehouse Notes */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <EditIcon className="w-3 h-3 text-slate-400" />
                  <span className="text-xs font-medium text-slate-600">Warehouse Notes</span>
                </div>
                {!isEditingWarehouseNotes && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingWarehouseNotes(true)}
                    className="h-6 px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
                  >
                    <EditIcon className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              
              {isEditingWarehouseNotes ? (
                <div className="space-y-2">
                  <textarea
                    className="w-full bg-white border border-slate-200 rounded-md p-2 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs transition-colors"
                    placeholder="Add warehouse notes..."
                    value={internalNotes || ''}
                    onChange={handleInternalNotesChange}
                    onBlur={() => {
                      handleInternalNotesBlur();
                      setIsEditingWarehouseNotes(false);
                    }}
                    autoFocus
                    rows={3}
                    style={{ maxHeight: '6rem', overflow: 'auto' }}
                  />
                  <div className="flex space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleInternalNotesBlur();
                        setIsEditingWarehouseNotes(false);
                      }}
                      className="h-6 px-2 py-1 text-xs"
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingWarehouseNotes(false)}
                      className="h-6 px-2 py-1 text-xs text-slate-500"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {internalNotes && internalNotes.trim() ? (
                    <div className="text-xs text-slate-700 bg-white rounded-md p-2 border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors" 
                         style={{ whiteSpace: 'pre-line' }}
                         onClick={() => setIsEditingWarehouseNotes(true)}>
                      {internalNotes}
                    </div>
                  ) : (
                    <div 
                      className="text-xs text-slate-400 italic cursor-pointer hover:text-slate-600 transition-colors"
                      onClick={() => setIsEditingWarehouseNotes(true)}
                    >
                      Click to add warehouse notes...
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        hidden
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={handleImportExcel}
      />

      <main className="mx-6">
        {showProgressBar && (
          <Card className="mb-4 bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Badge variant="info" className="font-medium">
                  Box Contents Submitting...
                </Badge>
                <span className="text-sm font-semibold text-slate-700">
                  {progress}%
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                                          className="h-full bg-blue-600 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        <div 
          className="resizable-container relative flex bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" 
          style={{ 
            minHeight: '70vh',
            position: 'relative',
          }}
        >
          {canViewLeftPanel() && (
            <div 
              className="relative border-r border-slate-200"
              style={{ 
                width: `${leftColumnWidth}%`,
                transition: isResizing ? 'none' : 'width 0.1s ease',
                flexGrow: isBoxPanelReadOnly() ? 1 : 0,
              }}
            >
            {/* Header Section - Compact but Functional Design */}
            <div className="bg-white border-b border-gray-200">
              {/* Two-row layout for better organization */}
              
              {/* Row 1: Search and Primary Filters */}
              <div className="px-4 py-2 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  {/* Search Bar - Takes up good amount of space */}
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder="Search by name, ASIN, SKU, FNSKU, UPC, EAN..."
                      value={itemSearchTerm}
                      onChange={(e) => setItemSearchTerm(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="text-sm font-medium text-gray-600">Status:</label>
                    <div className="relative status-dropdown-container">
                      <button
                        type="button"
                        className="min-w-32 px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between"
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      >
                        <span className="truncate">
                          {statusFilter.includes('All') || statusFilter.length === 0 
                            ? 'All' 
                            : statusFilter.length === 1 
                              ? statusFilter[0] 
                              : `${statusFilter.length} selected`
                          }
                        </span>
                        <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
                      </button>
                      {showStatusDropdown && (
                        <div className="absolute z-50 mt-1 w-56 bg-white border border-gray-300 rounded shadow-lg">
                          {[
                            { value: 'All', label: `All (${preStatusFilteredItems.length})` },
                            { value: 'Not Started', label: `Not Started (${statusCounts.NotStarted})` },
                            { value: 'In Progress', label: `In Progress (${statusCounts.InProgress})` },
                            { value: 'Completed', label: `Completed (${statusCounts.Completed})` },
                            { value: 'Over Limit', label: `Over Limit (${statusCounts.OverLimit})` }
                          ].map((status) => (
                            <label
                              key={status.value}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={statusFilter.includes(status.value)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  let newStatusFilter;
                                  if (status.value === 'All') {
                                    newStatusFilter = ['All'];
                                  } else {
                                    if (statusFilter.includes('All')) {
                                      newStatusFilter = [status.value];
                                    } else if (statusFilter.includes(status.value)) {
                                      newStatusFilter = statusFilter.filter(s => s !== status.value);
                                      if (newStatusFilter.length === 0) {
                                        newStatusFilter = ['All'];
                                      }
                                    } else {
                                      newStatusFilter = [...statusFilter.filter(s => s !== 'All'), status.value];
                                    }
                                  }
                                  setStatusFilter(newStatusFilter);
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span>{status.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Filter Controls */}
                  <div className="flex items-center gap-4 flex-shrink-0">


                    {/* Advanced Filters Toggle */}
                    <button
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      className={`px-3 py-1.5 text-sm border rounded transition-colors ${
                        showAdvancedFilters || prepFilter || shelvedFilter || fdaFilter !== null || priorNoticeFilter !== null
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <SettingsIcon className="w-4 h-4 mr-1" />
                      Advanced
                      {(prepFilter || shelvedFilter || fdaFilter !== null || priorNoticeFilter !== null) && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                          {[prepFilter, shelvedFilter, fdaFilter !== null, priorNoticeFilter !== null].filter(Boolean).length}
                        </span>
                      )}
                    </button>

                    {/* Clear Filters Button */}
                    {(prepFilter || shelvedFilter || fdaFilter !== null || priorNoticeFilter !== null || selectedPackingGroupFilter || (!statusFilter.includes('All') && statusFilter.length > 0)) && (
                      <button
                        onClick={resetAllFilters}
                        className="px-2 py-1.5 text-sm text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded transition-colors"
                      >
                        Clear All
                      </button>
                    )}
                  </div>


                </div>
              </div>

              {/* Advanced Filters Panel */}
              {showAdvancedFilters && (
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">Advanced Filters</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">
                          Combine filters below • Clear preset filter to use individual filters
                        </span>
                        <button
                          onClick={() => setShowFilterHelp(!showFilterHelp)}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          {showFilterHelp ? 'Hide Help' : 'Show Help'}
                        </button>
                      </div>
                    </div>

                                         {showFilterHelp && (
                       <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                         <div className="font-medium mb-1">Filter Explanations:</div>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                           <div>
                             <div className="font-medium mb-1">🔧 Quick Filters:</div>
                             <ul className="space-y-0.5 text-blue-700 text-xs">
                               <li><strong>Has Prep • No Shelves:</strong> Items that need prep work but aren't on shelves yet</li>
                               <li><strong>Has Shelves • No Prep:</strong> Items on shelves that don't need prep work</li>
                               <li><strong>Partial Shelves • Full Prep:</strong> Items with prep requirements but only partially shelved</li>
                             </ul>
                           </div>
                           <div>
                             <div className="font-medium mb-1">📋 FDA & Prior Notice:</div>
                             <ul className="space-y-0.5 text-blue-700 text-xs">
                               <li><strong>Prior Notice • No Shelves:</strong> Food items needing FDA prior notice but not shelved</li>
                               <li><strong>Prior Notice • Needs Prep:</strong> Food items needing both FDA prior notice and prep</li>
                               <li><strong>FDA Required • No Prior Notice:</strong> FDA items that don't need prior notice (like cosmetics)</li>
                               <li><strong>Missing All Info:</strong> Items with no prep, shelving, or FDA data</li>
                             </ul>
                           </div>
                         </div>
                         <div className="mt-1 pt-1 border-t border-blue-200 text-blue-600 text-xs">
                           <strong>Note:</strong> FDA and Prior Notice detection uses product title keywords (food, supplement, cosmetic, etc.) 
                           as a fallback when classification data isn't available.
                         </div>
                       </div>
                     )}
                    

                       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                         {/* Prep Filters */}
                         <div className="space-y-1">
                           <div className="text-xs font-medium text-slate-600 mb-1">🔧 Prep Status</div>
                           <div className="space-y-0.5">
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="prepFilter"
                                 checked={!prepFilter}
                                 onChange={() => setPrepFilter(false)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">All Items</span>
                             </label>
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="prepFilter"
                                 checked={prepFilter}
                                 onChange={() => setPrepFilter(true)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">Has Prep Required</span>
                             </label>
                           </div>
                         </div>

                         {/* Shelving Filters */}
                         <div className="space-y-1">
                           <div className="text-xs font-medium text-slate-600 mb-1">📦 Shelving Status</div>
                           <div className="space-y-0.5">
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="shelvedFilter"
                                 checked={!shelvedFilter}
                                 onChange={() => setShelvedFilter(false)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">All Items</span>
                             </label>
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="shelvedFilter"
                                 checked={shelvedFilter}
                                 onChange={() => setShelvedFilter(true)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">Has Shelves</span>
                             </label>
                           </div>
                         </div>

                         {/* FDA Filters */}
                         <div className="space-y-1">
                           <div className="text-xs font-medium text-slate-600 mb-1">🏛️ FDA Status</div>
                           <div className="space-y-0.5">
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="fdaFilter"
                                 checked={fdaFilter === null}
                                 onChange={() => setFdaFilter(null)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">All Items</span>
                             </label>
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="fdaFilter"
                                 checked={fdaFilter === true}
                                 onChange={() => setFdaFilter(true)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">FDA Required</span>
                             </label>
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="fdaFilter"
                                 checked={fdaFilter === false}
                                 onChange={() => setFdaFilter(false)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">No FDA Required</span>
                             </label>
                           </div>
                         </div>

                         {/* Prior Notice Filters */}
                         <div className="space-y-1">
                           <div className="text-xs font-medium text-slate-600 mb-1">📋 Prior Notice</div>
                           <div className="space-y-0.5">
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="priorNoticeFilter"
                                 checked={priorNoticeFilter === null}
                                 onChange={() => setPriorNoticeFilter(null)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">All Items</span>
                             </label>
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="priorNoticeFilter"
                                 checked={priorNoticeFilter === true}
                                 onChange={() => setPriorNoticeFilter(true)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">Prior Notice Required</span>
                             </label>
                             <label className="flex items-center gap-3 text-xs cursor-pointer hover:bg-slate-100 px-2 py-1 rounded">
                               <input
                                 type="radio"
                                 name="priorNoticeFilter"
                                 checked={priorNoticeFilter === false}
                                 onChange={() => setPriorNoticeFilter(false)}
                                 className="w-3 h-3 text-blue-600 flex-shrink-0"
                               />
                               <span className="text-slate-700">No Prior Notice</span>
                             </label>
                           </div>
                         </div>
                       </div>
                     )}
                  </div>
                </div>
              )}

              {/* Row 2: Pagination and Controls */}
              <div className="px-4 py-2 bg-gray-50">
                <div className="flex items-center justify-between">
                  {/* Left side - Items per page and count */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Show:</label>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-sm text-gray-600">per page</span>
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredShipmentItems.length)} of {filteredShipmentItems.length} items
                      {(prepFilter || shelvedFilter || fdaFilter !== null || priorNoticeFilter !== null || selectedPackingGroupFilter) && (
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Filtered from {itemsToFilter.length}
                        </span>
                      )}
                    </div>
                    

                  </div>



                  {/* Right side - Pagination */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="First page"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Previous page"
                    >
                      Prev
                    </button>
                    <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium border border-blue-600 rounded min-w-[2.5rem] text-center">
                      {currentPage}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage >= totalPages}
                      className="px-2 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Next page"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage >= totalPages}
                      className="px-2 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Last page"
                    >
                      Last
                    </button>
                  </div>
                </div>
              </div>

              {/* Packing Group Filter and Bulk Print Controls */}
              {packingGroupFilterOptions.length > 1 ? (
                <div className="px-4 py-2 bg-blue-50 border-b border-blue-200">
                  <div className="flex items-center justify-between gap-3">
                    {/* Left: Packing Group Filter */}
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-blue-800">Filter by Packing Group:</label>
                      <select
                        value={selectedPackingGroupFilter ? packingGroupFilterOptions.findIndex(opt => opt.value.id === selectedPackingGroupFilter.id) : ''}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            setSelectedPackingGroupFilter(null);
                          } else {
                            const selectedOption = packingGroupFilterOptions[parseInt(e.target.value)];
                            setSelectedPackingGroupFilter(selectedOption.value);
                          }
                        }}
                        className="px-2 py-1.5 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white w-64"
                      >
                        <option value="">All Items ({itemsBeforePackingGroupFilter})</option>
                        {packingGroupFilterOptions.map((option, index) => (
                          <option key={option.value.id} value={index}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {selectedPackingGroupFilter && (
                        <button
                          onClick={() => setSelectedPackingGroupFilter(null)}
                          className="px-2 py-1 text-xs bg-blue-200 text-blue-800 rounded hover:bg-blue-300 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    {/* Right: Bulk Print Controls */}
                    <div className="flex items-center gap-2">
                      {selectedItems.size > 0 && (
                        <button
                          onClick={handleClearSelection}
                          className="px-2 py-1 text-xs text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded transition-colors"
                          title="Clear selection"
                        >
                          Clear ({selectedItems.size})
                        </button>
                      )}
                      <button
                        onClick={handleBulkPrint}
                        disabled={selectedItems.size === 0}
                        className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                          selectedItems.size > 0
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                        title={selectedItems.size > 0 ? `Bulk print ${selectedItems.size} items` : 'Select items to enable bulk print'}
                      >
                        <PrintIcon className="w-4 h-4 mr-1" />
                        Print {selectedItems.size > 0 ? `(${selectedItems.size})` : ''}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Show bulk print controls even when there's no packing group filter
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-end gap-2">
                    {selectedItems.size > 0 && (
                      <button
                        onClick={handleClearSelection}
                        className="px-2 py-1 text-xs text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded transition-colors"
                        title="Clear selection"
                      >
                        Clear ({selectedItems.size})
                      </button>
                    )}
                    <button
                      onClick={handleBulkPrint}
                      disabled={selectedItems.size === 0}
                      className={`px-2 py-1 text-sm font-medium rounded transition-colors ${
                        selectedItems.size > 0
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                      title={selectedItems.size > 0 ? `Bulk print ${selectedItems.size} items` : 'Select items to enable bulk print'}
                    >
                      <PrintIcon className="w-4 h-4 mr-1" />
                      Print {selectedItems.size > 0 ? `(${selectedItems.size})` : ''}
                    </button>
                  </div>
                </div>
              )}

              {/* Table Header */}
              <div className="bg-gray-50 border-b border-gray-200">
                <div className="flex text-xs font-medium text-gray-700 uppercase tracking-wider">
                  <div 
                    className="px-2 py-3 cursor-pointer hover:bg-gray-100 select-none flex items-center gap-1" 
                    style={{ width: '120px' }}
                    onClick={handleQuantitySort}
                    title="Click to sort by quantity"
                  >
                    {/* Select All Checkbox */}
                    <div className="flex items-center mr-2">
                      <input
                        type="checkbox"
                        checked={currentItems.length > 0 && currentItems.every(item => selectedItems.has(item.id))}
                        onChange={(e) => {
                          const currentItemIds = currentItems.map(item => item.id);
                          if (e.target.checked) {
                            // Add all current page items to selection
                            setSelectedItems(prev => {
                              const newSelection = new Set(prev);
                              currentItemIds.forEach(id => newSelection.add(id));
                              return newSelection;
                            });
                          } else {
                            // Remove all current page items from selection
                            setSelectedItems(prev => {
                              const newSelection = new Set(prev);
                              currentItemIds.forEach(id => newSelection.delete(id));
                              return newSelection;
                            });
                          }
                        }}
                        className="w-3 h-3 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                        title="Select/deselect all visible items"
                      />
                    </div>
                    <span>QTY</span>
                    {quantitySortOrder === 'asc' && <ChevronUp className="w-3 h-3" />}
                    {quantitySortOrder === 'desc' && <ChevronDown className="w-3 h-3" />}
                    {quantitySortOrder === null && <div className="w-3 h-3 opacity-30"><ChevronUp className="w-3 h-3" /></div>}
                  </div>
                  <div className="px-2 py-3" style={{ width: '240px' }}>PRODUCT DETAILS</div>
                  <div className="px-2 py-3" style={{ width: '150px' }}>SHELVING</div>
                  <div className="px-2 py-3" style={{ width: '150px' }}>PREP & SERVICES</div>
                  <div className="px-2 py-3" style={{ width: '120px' }}>EXP/WEIGHT</div>
                  <div className="px-2 py-3" style={{ width: '100px' }}>ACTIONS</div>
                </div>
              </div>
            </div>
            {/* Table Body */}
            <div className="bg-white overflow-y-auto overflow-x-hidden" style={{ height: 'calc(100vh - 280px)' }}>
              {currentItems.map((item, index) => {
                return (
                  <OutboundItem
                    key={item.item.merchant_sku} // Use a stable key based on merchant_sku
                    item={item}
                    selectedNickname={selectedNickname}
                    quantityInBoxes={quantitiesInBoxes[item.item.merchant_sku] || 0}
                    asinData={asinData}
                    shelvingData={shelvingData.filter(data => data.SKU === item.item.merchant_sku)}
                    updateShelvingQty={updateShelvingQty}
                    removeShelving={removeShelving}
                    servicesData={servicesData}
                    clientsData={clientsData}
                    country={clientInfo.country}
                    availablePrinters={availablePrinters}
                    loadingPrinters={loadingPrinters}
                    autoPrintEnabled={autoPrintEnabled}
                    // Bulk selection props
                    isSelected={selectedItems.has(item.id)}
                    onSelect={(isSelected) => handleItemSelect(item.id, isSelected)}
                  />
                );
              })}
            </div>


          </div>
          )}
          
          {canViewLeftPanel() && !isBoxPanelReadOnly() && (
            <div
              className="resizer group"
              onMouseDown={startResizing}
              style={{
                position: 'absolute',
                right: `${100 - leftColumnWidth}%`,
                top: 0,
                bottom: 0,
                width: '12px',
                cursor: 'col-resize',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'translateX(6px)',
              }}
            >
            <div
              className={`w-1 h-full transition-colors duration-200 ${
                isResizing ? 'bg-blue-500' : 'bg-slate-300 group-hover:bg-slate-400'
              }`}
            />
          </div>
          )}

          <div 
            style={{ 
              width: canViewLeftPanel() ? `${100 - leftColumnWidth}%` : '100%',
              transition: isResizing ? 'none' : 'width 0.1s ease',
              flexShrink: isBoxPanelReadOnly() ? 0 : 1,
            }}
            className={isBoxPanelReadOnly() ? 'filter grayscale pointer-events-none' : ''}
          >
            {/* Boxes Management Panel */}
            <BoxesPanel
              boxes={sortedDbData}
              onAddBox={handleAddBox}
              onDuplicateBox={handleDuplicateBox}
              onUpdateBoxDetails={handleUpdateBoxDetails}
                  shipmentItems={shipmentItems}
                  asinData={asinData}
                  selectedNickname={selectedNickname}
                  clientCountry={clientInfo.country}
              standardBoxes={standardBoxes}
              fetchStandardBoxes={fetchStandardBoxes}
              canEditItemDetails={canEditLeftPanel()}
              currentRole={currentRole}
              shipmentId={shipmentId}
              autoPrintEnabled={autoPrintEnabled}
              onBoxSelect={(boxNumber, box) => {
                console.log('Box selected:', boxNumber, box);
                openBoxNumberRef.current = boxNumber;
                selectedBoxRef.current = box;
              }}
              disableScanner={bookModeEnabled}
                />
          </div>
        </div>
      </main>

      <Dialog open={showShelfReminder} onOpenChange={setShowShelfReminder}>
        <DialogContent className="max-w-lg mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-center">Confirm Shelf Removal</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 text-center">
              Please confirm that items have already been removed from the shelves before submitting the box
              contents.
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowShelfReminder(false)}
            >
              Go Back
            </Button>
            <Button
              onClick={() => {
                setShowShelfReminder(false);
                handleSubmitBoxContents(true, true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[85vh] bg-white overflow-hidden mx-4">
          <div className="flex flex-col h-full max-h-[80vh]">
            {/* Header */}
            <DialogHeader className="border-b border-gray-200 pb-3">
              <div className="flex items-center space-x-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  oversizedItemsWithoutFees.length === 0 
                    ? 'bg-green-100' 
                    : 'bg-amber-100'
                }`}>
                  <span className={oversizedItemsWithoutFees.length === 0 ? 'text-green-600' : 'text-amber-600'}>
                    {oversizedItemsWithoutFees.length === 0 ? '✅' : '⚠️'}
                  </span>
                </div>
                <div>
                  <DialogTitle className="text-lg font-semibold text-gray-900">
                    {(() => {
                      // Check how many items are resolved
                      const resolvedCount = oversizedItemsWithoutFees.filter(item => {
                        const latestItem = shipmentItems?.find(si => si.id === item.id) || item;
                        const lengthInches = item.item.length_mm / 25.4;
                        const widthInches = item.item.width_mm / 25.4;
                        const heightInches = item.item.height_mm / 25.4;
                        const isHugeOversized = (lengthInches >= 25 || widthInches >= 25 || heightInches >= 25);
                        
                        const hasOversizedFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                          sl.service.name.toLowerCase().includes('oversized') || 
                          sl.service.name.toLowerCase().includes('oversize') || 
                          sl.service.name.toLowerCase().includes('huge')
                        );
                        const hasHugeFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                          sl.service.name.toLowerCase().includes('huge')
                        );
                        
                        return (isHugeOversized && hasHugeFee) || (!isHugeOversized && hasOversizedFee && !hasHugeFee);
                      }).length;

                      const totalCount = oversizedItemsWithoutFees.length;
                      const allResolved = resolvedCount === totalCount;

                      if (allResolved) {
                        return '✅ All Items Resolved';
                      } else {
                        return `Oversized Items (${resolvedCount}/${totalCount} resolved)`;
                      }
                    })()}
                  </DialogTitle>
                  <p className="text-xs text-gray-600">
                    {(() => {
                      // Check how many items are resolved
                      const resolvedCount = oversizedItemsWithoutFees.filter(item => {
                        const latestItem = shipmentItems?.find(si => si.id === item.id) || item;
                        const lengthInches = item.item.length_mm / 25.4;
                        const widthInches = item.item.width_mm / 25.4;
                        const heightInches = item.item.height_mm / 25.4;
                        const isHugeOversized = (lengthInches >= 25 || widthInches >= 25 || heightInches >= 25);
                        
                        const hasOversizedFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                          sl.service.name.toLowerCase().includes('oversized') || 
                          sl.service.name.toLowerCase().includes('oversize') || 
                          sl.service.name.toLowerCase().includes('huge')
                        );
                        const hasHugeFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                          sl.service.name.toLowerCase().includes('huge')
                        );
                        
                        return (isHugeOversized && hasHugeFee) || (!isHugeOversized && hasOversizedFee && !hasHugeFee);
                      }).length;

                      const totalCount = oversizedItemsWithoutFees.length;
                      const allResolved = resolvedCount === totalCount;

                      if (allResolved) {
                        return "All oversized items now have the correct fees applied";
                      } else {
                        return `${totalCount - resolvedCount} item${totalCount - resolvedCount > 1 ? 's' : ''} still need oversized fees`;
                      }
                    })()}
                  </p>
                </div>
              </div>
            </DialogHeader>

            {/* Content */}
            <div className="flex-1 py-2 overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-700">
                  Items missing oversized fees:
                </div>
                {isRefreshingData && (
                  <div className="text-xs text-blue-600 flex items-center">
                    <div className="w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin mr-1"></div>
                    Refreshing...
                  </div>
                )}
              </div>
              
              {/* Items List */}
              <div className="bg-gray-50 rounded p-2 overflow-y-auto" style={{ maxHeight: '50vh' }}>
                <div className="space-y-2">
                  {oversizedItemsWithoutFees.map((item, index) => {
                    // Get the latest item data from shipmentItems to ensure we see updated services
                    const latestItem = shipmentItems?.find(si => si.id === item.id) || item;
                    
                    // Debug: Log service differences
                    if (item.service_lines?.length !== latestItem.service_lines?.length) {
                      console.log(`🔄 [${item.item.merchant_sku}] Services updated:`, {
                        original: item.service_lines?.map(sl => sl.service.name) || [],
                        latest: latestItem.service_lines?.map(sl => sl.service.name) || []
                      });
                    }
                    // Calculate for display in cm/kg
                    const lengthCm = item.item.length_mm / 10;
                    const widthCm = item.item.width_mm / 10;
                    const heightCm = item.item.height_mm / 10;
                    const weightKg = item.item.weight_gm / 1000;
                    
                    // Still need inches for oversized logic
                    const lengthInches = item.item.length_mm / 25.4;
                    const widthInches = item.item.width_mm / 25.4;
                    const heightInches = item.item.height_mm / 25.4;
                    const isHugeOversized = (lengthInches >= 25 || widthInches >= 25 || heightInches >= 25);
                    const expectedFee = isHugeOversized ? 'Huge Oversized' : 'Oversized';
                    
                    // Check if item is correctly configured - move this up to card level
                    const hasOversizedFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                      sl.service.name.toLowerCase().includes('oversized') || 
                      sl.service.name.toLowerCase().includes('oversize') || 
                      sl.service.name.toLowerCase().includes('huge')
                    );
                    const hasHugeFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                      sl.service.name.toLowerCase().includes('huge')
                    );
                    const isCorrectlyConfigured = (isHugeOversized && hasHugeFee) || (!isHugeOversized && hasOversizedFee && !hasHugeFee);
                    
                    return (
                      <div key={index} className={`bg-white rounded p-2 shadow-sm ${
                        isCorrectlyConfigured 
                          ? 'border-2 border-green-200 bg-green-50' 
                          : 'border border-gray-200'
                      }`}>
                         {/* Compact Item Layout */}
                         <div className="flex items-start space-x-2">
                           {/* Product Image */}
                           <div className="w-12 h-12 bg-gray-100 border border-gray-200 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                             {item.item.images && item.item.images.length > 0 && item.item.images[0].large_url ? (
                               <img 
                                 src={item.item.images[0].large_url} 
                                 alt={item.item.title}
                                 className="w-full h-full object-contain"
                                 onError={(e) => {
                                   e.target.style.display = 'none';
                                   e.target.nextSibling.style.display = 'flex';
                                 }}
                               />
                             ) : null}
                             <div className={`w-full h-full bg-gray-100 flex items-center justify-center ${item.item.images && item.item.images.length > 0 ? 'hidden' : 'flex'}`}>
                               <span className="text-gray-400 text-xs">No Img</span>
                             </div>
                           </div>
                           
                           {/* Content */}
                           <div className="flex-1 min-w-0 space-y-1">
                             {/* Title and Fee Badge */}
                             <div className="flex items-start justify-between">
                               <div className="text-xs font-medium text-gray-900 leading-tight flex-1 pr-2 break-words">
                                 {item.item.title}
                               </div>
                               <div className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                 isHugeOversized 
                                   ? 'bg-red-100 text-red-800' 
                                   : 'bg-orange-100 text-orange-800'
                               }`}>
                                 {expectedFee}
                               </div>
                             </div>
                             
                             {/* SKU, Dimensions */}
                             <div className="text-xs text-gray-500 mb-1">
                               <span className="font-mono">{item.item.merchant_sku}</span>
                               <span className="mx-1">•</span>
                               <span className="font-mono">{lengthCm.toFixed(1)} × {widthCm.toFixed(1)} × {heightCm.toFixed(1)} cm</span>
                               <span className="mx-1">•</span>
                               <span>{weightKg.toFixed(2)} kg</span>
                             </div>
                             
                             {/* Services */}
                             <div className="flex flex-wrap gap-1 mb-2">
                               {latestItem.service_lines && latestItem.service_lines.length > 0 ? (
                                 latestItem.service_lines.map((sl, idx) => (
                                   <span key={idx} className={`text-xs px-1 py-0.5 rounded ${
                                     sl.service.charge && sl.service.charge > 0
                                       ? 'bg-green-100 text-green-700' 
                                       : 'bg-gray-100 text-gray-600'
                                   }`}>
                                     {sl.service.name}
                                   </span>
                                 ))
                               ) : (
                                 <span className="text-xs text-gray-400 italic">No services</span>
                               )}
                             </div>
                             
                             {/* Action Button */}
                             <div>
                               {(() => {
                                 
                                 // Check loading and completed states
                                 const swapKey = `${item.id}-swap`;
                                 const hugeKey = `${item.id}-huge`;
                                 const oversizedKey = `${item.id}-oversized`;
                                 
                                 const isSwapLoading = loadingServices[swapKey];
                                 const isHugeLoading = loadingServices[hugeKey];
                                 const isOversizedLoading = loadingServices[oversizedKey];
                                 
                                 const isSwapCompleted = completedServices[swapKey];
                                 const isHugeCompleted = completedServices[hugeKey];
                                 const isOversizedCompleted = completedServices[oversizedKey];
                                 
                                 // Show resolved state if correctly configured
                                 if (isCorrectlyConfigured) {
                                   return (
                                     <div className="flex items-center space-x-2">
                                       <div className="bg-green-100 border border-green-300 rounded px-2 py-1 flex items-center space-x-1">
                                         <span className="text-green-600">✅</span>
                                         <span className="text-xs text-green-700 font-medium">Resolved</span>
                                       </div>
                                     </div>
                                   );
                                 } else if (hasOversizedFee && isHugeOversized && !hasHugeFee) {
                                   // Has oversized fee but should be huge oversized
                                   return (
                                     <button 
                                       onClick={() => handleSwapToHugeOversized(item)}
                                       disabled={isSwapLoading}
                                       className={`text-xs px-2 py-1 rounded min-w-[120px] flex items-center justify-center ${
                                         isSwapCompleted
                                           ? 'bg-green-600 text-white'
                                           : isSwapLoading
                                           ? 'bg-gray-400 text-white cursor-not-allowed'
                                           : 'bg-orange-600 hover:bg-orange-700 text-white'
                                       }`}
                                     >
                                       {isSwapCompleted ? (
                                         <>✓ Swapped</>
                                       ) : isSwapLoading ? (
                                         <>⏳ Swapping...</>
                                       ) : (
                                         <>Swap to Huge Oversized</>
                                       )}
                                     </button>
                                   );
                                 } else if (!hasOversizedFee) {
                                   // No oversized fee, add the appropriate one
                                   if (isHugeOversized) {
                                     return (
                                       <button 
                                         onClick={() => handleAddHugeOversized(item)}
                                         disabled={isHugeLoading}
                                         className={`text-xs px-2 py-1 rounded min-w-[120px] flex items-center justify-center ${
                                           isHugeCompleted
                                             ? 'bg-green-600 text-white'
                                             : isHugeLoading
                                             ? 'bg-gray-400 text-white cursor-not-allowed'
                                             : 'bg-red-600 hover:bg-red-700 text-white'
                                         }`}
                                       >
                                         {isHugeCompleted ? (
                                           <>✓ Added</>
                                         ) : isHugeLoading ? (
                                           <>⏳ Adding...</>
                                         ) : (
                                           <>Add Huge Oversized</>
                                         )}
                                       </button>
                                     );
                                   } else {
                                     return (
                                       <button 
                                         onClick={() => handleAddOversized(item)}
                                         disabled={isOversizedLoading}
                                         className={`text-xs px-2 py-1 rounded min-w-[120px] flex items-center justify-center ${
                                           isOversizedCompleted
                                             ? 'bg-green-600 text-white'
                                             : isOversizedLoading
                                             ? 'bg-gray-400 text-white cursor-not-allowed'
                                             : 'bg-orange-600 hover:bg-orange-700 text-white'
                                         }`}
                                       >
                                         {isOversizedCompleted ? (
                                           <>✓ Added</>
                                         ) : isOversizedLoading ? (
                                           <>⏳ Adding...</>
                                         ) : (
                                           <>Add Oversized</>
                                         )}
                                       </button>
                                     );
                                   }
                                 }
                                 return null;
                               })()}
                             </div>
                           </div>
                         </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Help Section */}
            <div className="border-t border-gray-200 pt-2">
              {(() => {
                // Check how many items are resolved
                const resolvedCount = oversizedItemsWithoutFees.filter(item => {
                  const latestItem = shipmentItems?.find(si => si.id === item.id) || item;
                  const lengthInches = item.item.length_mm / 25.4;
                  const widthInches = item.item.width_mm / 25.4;
                  const heightInches = item.item.height_mm / 25.4;
                  const isHugeOversized = (lengthInches >= 25 || widthInches >= 25 || heightInches >= 25);
                  
                  const hasOversizedFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                    sl.service.name.toLowerCase().includes('oversized') || 
                    sl.service.name.toLowerCase().includes('oversize') || 
                    sl.service.name.toLowerCase().includes('huge')
                  );
                  const hasHugeFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                    sl.service.name.toLowerCase().includes('huge')
                  );
                  
                  return (isHugeOversized && hasHugeFee) || (!isHugeOversized && hasOversizedFee && !hasHugeFee);
                }).length;

                const totalCount = oversizedItemsWithoutFees.length;
                const allResolved = resolvedCount === totalCount;

                if (allResolved) {
                  return (
                    <div className="bg-green-50 rounded p-2 mb-2">
                      <div className="text-xs text-green-800">
                        <strong>✅ All Resolved!</strong> All {totalCount} item{totalCount > 1 ? 's have' : ' has'} the correct oversized fees applied. You can continue with submission.
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="bg-blue-50 rounded p-2 mb-2">
                      <div className="text-xs text-blue-800">
                        <strong>📋 Progress:</strong> {resolvedCount}/{totalCount} items resolved. {totalCount - resolvedCount} still need oversized fees.
                      </div>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Footer */}
            <DialogFooter className="flex justify-between items-center pt-2 border-t border-gray-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsModalOpen(false)}
                className="text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                ← Go Back
              </Button>
              
              <div className="flex space-x-2">
                {(() => {
                  // Check if all items are correctly configured
                  const allItemsResolved = oversizedItemsWithoutFees.every(item => {
                    const latestItem = shipmentItems?.find(si => si.id === item.id) || item;
                    const lengthInches = item.item.length_mm / 25.4;
                    const widthInches = item.item.width_mm / 25.4;
                    const heightInches = item.item.height_mm / 25.4;
                    const isHugeOversized = (lengthInches >= 25 || widthInches >= 25 || heightInches >= 25);
                    
                    const hasOversizedFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                      sl.service.name.toLowerCase().includes('oversized') || 
                      sl.service.name.toLowerCase().includes('oversize') || 
                      sl.service.name.toLowerCase().includes('huge')
                    );
                    const hasHugeFee = latestItem.service_lines && latestItem.service_lines.some(sl => 
                      sl.service.name.toLowerCase().includes('huge')
                    );
                    
                    return (isHugeOversized && hasHugeFee) || (!isHugeOversized && hasOversizedFee && !hasHugeFee);
                  });

                  if (allItemsResolved) {
                    // All items are resolved - show continue button
                    return (
                      <Button
                        size="sm"
                        onClick={() => {
                          setLoadingServices({});
                          setCompletedServices({});
                          setIsModalOpen(false);
                          handleSubmitBoxContents(isValidationConfirmed, false, currentSubmissionPlan);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        ✓ Continue to Submission
                      </Button>
                    );
                  } else {
                    // Some items still need work - show refresh and override buttons
                    return (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            setIsRefreshingData(true);
                            await reloadShipmentData();
                            setTimeout(() => {
                              setIsRefreshingData(false);
                              checkOversizedItemsWithoutFees();
                              console.log('Manual refresh completed - services list updated');
                            }, 1000);
                          }}
                          disabled={isRefreshingData}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                        >
                          <RefreshIcon className="w-3 h-3 mr-1" />
                          {isRefreshingData ? 'Refreshing...' : 'Refresh Now'}
                        </Button>
                        
                        <Button
                          size="sm"
                          onClick={() => {
                            setIsModalOpen(false);
                            handleSubmitBoxContents(true, true, currentSubmissionPlan);
                          }}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Continue Anyway
                        </Button>
                      </>
                    );
                  }
                })()}
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <SplitShipmentModal
        open={showSplitShipmentModal}
        onClose={() => setShowSplitShipmentModal(false)}
        shipmentItems={shipmentItems}
        onSelectItems={(items) => setSelectedSplitItems(items)}
        onSplit={handleSplitShipment}
      />
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>Shipment Split Successful</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <p className="text-slate-600">The shipment has been successfully split!</p>
            <p className="text-slate-600">Would you like to:</p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowSuccessModal(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                window.open(
                  `https://app.yyzprep.ca/staff/shipments/outbound/${newOutboundId}`,
                  '_blank'
                );
                window.open(`/outbound/${newOutboundId}`, '_blank');
                setShowSuccessModal(false);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Open New Shipment Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showSendEmailModal && (
        <SendEmail
          show={showSendEmailModal}
          recipientEmail={clientInfo.email}
          onClose={() => setShowSendEmailModal(false)}
        />
      )}
              {showImportModal && (
          <ImportModal
            data={importedData}
            onClose={() => setShowImportModal(false)}
            onConfirm={handleConfirmImport}
          />
        )}
      {showOutboundImportModal && (
        <OutboundImportModal
          show={showOutboundImportModal}
          onClose={() => setShowOutboundImportModal(false)}
          currentShipmentId={shipmentId}
          currentShipmentItems={shipmentItems}
          currentTeamId={shipmentInfo?.team_id}
          servicesData={servicesData}
          onImportComplete={async () => {
            await reloadShipmentData();
            await refreshBoxes();
          }}
          showSnackbar={showSnackbar}
        />
      )}
      {showPickingListModal && (
        <PickingListModal
          show={showPickingListModal}
          onClose={() => setShowPickingListModal(false)}
          data={pickingListData}
        />
      )}
      {showReassignPlanModal && (
        <ReassignPlanModal
          show={showReassignPlanModal}
          shipmentId={shipmentId}
          onClose={() => setShowReassignPlanModal(false)}
          mainOutboundId={mainOutboundId}
          handleReassignPlan={handleReassignPlan}
        />
      )}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>Reset Box Contents</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to reset all box contents for <strong>FBA ID: {selectedPlan?.id}</strong>?
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetBoxContents} 
              disabled={isResetting}
              variant="destructive"
            >
              {isResetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <OptimizeModal
        open={showOptimizeModal}
        onClose={() => setShowOptimizeModal(false)}
        onComplete={async () => {
          // Trigger final refresh when user completes the optimization
          await reloadShipmentData();
          showSnackbar('Optimization complete! Quantities have been updated.', 'success');
        }}
        shipmentInfo={shipmentInfo}
        onOptimize={handleOptimize}
        calculateRemainingQuantities={calculateRemainingQuantities}
      />
      {/* Generate Plan Modal */}
      <Dialog 
        open={showGeneratePlanModal} 
        onOpenChange={(open) => !isGeneratingPlan && setShowGeneratePlanModal(open)}
      >
        <DialogContent className="max-w-lg mx-4">
          <DialogHeader>
            <DialogTitle>Generate Plan Required</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-600">
              This outbound doesn't have any plans yet. You need to generate a plan before you can proceed.
            </p>
            <div className="text-sm text-slate-600">
              <p className="mb-2">This will:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Create a new plan with all items</li>
                <li>Generate packing options</li>
                <li>Make the plan available for selection</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button 
              variant="outline"
              onClick={() => setShowGeneratePlanModal(false)} 
              disabled={isGeneratingPlan}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGeneratePlan}
              disabled={isGeneratingPlan}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isGeneratingPlan ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Plan...
                </>
              ) : (
                <>
                  <AutoFixHighIcon className="w-4 h-4 mr-2" />
                  Generate Plan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={Boolean(errorMessage)} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent className="max-w-lg mx-4">
          <DialogHeader>
            <DialogTitle className="text-red-600">Plan Generation Failed</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 whitespace-pre-line">
              {errorMessage}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setErrorMessage(null)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkFeesModal
        open={showBulkFeesModal}
        onClose={() => setShowBulkFeesModal(false)}
        shipmentItems={shipmentItems}
        servicesData={servicesData}
        updateItemServices={updateItemServices}
        showSnackbar={showSnackbar}
        reloadShipmentData={reloadShipmentData}
      />
      <PlanPlacementFeesModal
        open={showPlanPlacementFeesModal}
        onClose={() => setShowPlanPlacementFeesModal(false)}
        shipmentData={{ items: shipmentItems }}
        asinData={contextAsinData}
      />
      <PlanSelectionModal
        isOpen={showPlanSelectionModal}
        onClose={() => setShowPlanSelectionModal(false)}
        plans={plans}
        shipmentItems={shipmentItems}
        clientInfo={clientInfo}
        onSelectPlan={(plan) => {
          setShowPlanSelectionModal(false);
          setCurrentSubmissionPlan(plan);
          // Continue with submission after plan selection
          console.log('Plan selected from modal:', plan?.id);
          handleSubmitBoxContents(true, false, plan);
        }}
      />

      {/* Add Validation Errors Modal */}
      <Dialog open={showValidationErrorsModal} onOpenChange={setShowValidationErrorsModal}>
        <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[90vh] overflow-hidden mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <span className="text-red-600">⚠</span>
              Box Validation Errors
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 overflow-y-auto max-h-[60vh]">
            <p className="text-slate-600 mb-4">
              The following boxes have validation errors or warnings. You can proceed anyway, but please review:
            </p>
            <div className="space-y-4">
              {validationErrors.map((boxError, index) => (
                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="font-medium text-red-800 mb-2">
                    📦 Box {boxError.visualId}
                  </div>
                  <ul className="space-y-1">
                    {boxError.errors.map((error, errorIndex) => (
                      <li key={errorIndex} className="text-sm text-red-700 flex items-start gap-2">
                        <span className="text-red-500 mt-0.5">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="text-sm text-yellow-800">
                <strong>Note:</strong> Critical errors (like weight exceeding 21kg limit) should be fixed before proceeding. 
                Other warnings can be acknowledged if you're confident they're intentional.
              </div>
            </div>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowValidationErrorsModal(false)}
            >
              Cancel & Fix Issues
            </Button>
            <Button
              onClick={() => {
                setShowValidationErrorsModal(false);
                setIsValidationConfirmed(true);
                // Proceed with submission, acknowledging errors
                handleSubmitBoxContents(true, false, currentSubmissionPlan || selectedPlan);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Proceed Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Print Modal */}
      <BulkPrintModal
        isOpen={showBulkPrintModal}
        onClose={() => setShowBulkPrintModal(false)}
        selectedItems={selectedItems}
        shipmentItems={shipmentItems}
        availablePrinters={availablePrinters}
        autoPrintEnabled={autoPrintEnabled}
        clientInfo={clientInfo}
        quantitiesInBoxes={quantitiesInBoxes}
      />

      {/* Vision Detector Overlay */}
      {showVisionDetector && (
        <VisionDetectorOverlay
          shipmentItems={shipmentItems}
          onDetection={(detectedIdentifiers) => {
            setVisionDetectedItems(detectedIdentifiers);
          }}
          onClearFilter={() => {
            setVisionDetectedItems([]);
          }}
          hasActiveFilter={visionDetectedItems.length > 0}
          onClose={() => {
            setShowVisionDetector(false);
          }}
        />
      )}

      {/* Vision Filter Active Badge */}
      {visionDetectedItems.length > 0 && !showVisionDetector && (
        <div className="fixed bottom-4 left-4 z-40 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-3">
          <Camera className="w-5 h-5" />
          <span className="text-sm font-medium">
            Vision Filter: {visionDetectedItems.length} item{visionDetectedItems.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => setVisionDetectedItems([])}
            className="ml-2 hover:bg-blue-700 rounded p-1"
          >
            <span className="text-lg leading-none">&times;</span>
          </button>
        </div>
      )}

      {/* Book Mode Overlay */}
      {showBookModeOverlay && (
        <BookModeOverlay
          result={bookModeResult}
          onClose={() => {
            setShowBookModeOverlay(false);
            setBookModeResult(null);
          }}
          onSelectMatch={(selectedMatch) => {
            setShowBookModeOverlay(false);
            const activeBoxNumber = openBoxNumberRef.current || selectedBoxRef.current?.boxNumber;
            processBookModeItem(selectedMatch, bookModeResult?.scannedCode, activeBoxNumber);
          }}
        />
      )}

    </div>
  );
}
export default Shipment;
